import { pickCrossMakeSiblings } from "@/lib/link-rotation";
import { configLabels } from "@/lib/config-labels";
import { ConfigTag } from "@/components/ConfigTag";
import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { aliasedModelSlug } from "@/lib/slug-aliases";
import {
  getVehiclesForPage,
  getRelatedLinks,
  getSpecsForModel,
  getNhtsaExtrasForModel,
  type VehicleRow,
  type SpecRow,
  type DimensionRow,
  type SafetyRow,
} from "@/lib/vehicle-queries";
import {
  compactRows,
  capRows,
  capRowsPerYear,

  pickFields,
  stripProvenance,
  shortIds,
  takeProvenance,
  PROVENANCE_KEYS,
  type Provenance,
} from "@/lib/payload";
import { RelatedLinks } from "@/components/RelatedLinks";
import { EstimatedBadge } from "@/components/EstimatedBadge";
import { VerifiedBadge, publicSourceUrl } from "@/components/VerifiedBadge";
import { UnitToggle } from "@/components/UnitToggle";
import { useUnitSystem, formatLength, formatWeight, formatDistance, formatMpg, formatMpgValue } from "@/lib/units";
import { MaintenanceSchedule, MaintenanceDirectAnswer } from "@/components/MaintenanceSchedule";
import { MaintenanceSpecLink } from "@/components/MaintenanceSpecLink";
import { maintenanceSpecModelUrl } from "@/lib/maintenance-spec";
import { buildMaintenanceProfile, buildMaintenanceEconomics, type MaintenanceContext } from "@/lib/maintenance";
import { buildMaintenanceYearRows, sitemapWorthyYears, type MaintenanceYearRow } from "@/lib/maintenance-years";

import { breadcrumbLd, faqLd, SITE_OG_IMAGE, pageTitle, vehicleLd } from "@/lib/seo";
import { FaqBlock } from "@/components/FaqBlock";
import { buildQuickAnswer } from "@/lib/answers";
import { buildSpecTitleBase, specQuestionLead } from "@/lib/spec-title";
import {
  isIndexableModel,
  isIndexableDataType,
  isProvenPerformerPath,
  isIndexableAttributePage,
  countDifferentiators,
  NOINDEX_META,
} from "@/lib/index-policy";
import { getIsProvenPage, getIsHighDemandModel } from "@/lib/index-overrides.functions";
import { getAttributeSiblings } from "@/lib/attribute-siblings.functions";
import { UsefulTools } from "@/components/UsefulTools";
import { BlufSummary } from "@/components/BlufSummary";
import { CurbWeightContext, DimensionsContext } from "@/components/AttributeContext";
import { CurbWeightRebuild, useCurbWeightPage } from "@/components/CurbWeightRebuild";
import {
  isRebuiltCurbWeightPage,
  buildCurbWeightPage,
  type RebuildSpec,
  type RebuildDim,
  type RebuildVehicle,
  type ClassPosition,
} from "@/lib/curb-weight-rebuild";
import { getCurbWeightClassPosition } from "@/lib/curb-weight-peers.functions";
import { buildBlufSummary } from "@/lib/bluf";
import { buildSpecNarrative, buildSafetyNarrative } from "@/lib/spec-narrative";
import { buildDepthNarrative } from "@/lib/depth-narrative";
import { buildMaintenanceModelDepth } from "@/lib/maintenance-model-depth";
import { specBluf, specHeadline } from "@/lib/proprietary-copy";
import { RealWorldMpgTable, TowingBenchmarkTable } from "@/components/ExpertBenchmarks";

import {
  isValidDataType,
  isSpecDataType,
  isNhtsaDataType,
  EV_DATA_TYPE,
  getDataTypeInfo,
  ALL_DATA_TYPES,
  SPEC_DATA_TYPES,
  type DataTypeKey,
} from "@/lib/vehicle-data";

export const Route = createFileRoute("/$make/$model/$dataType")({
  // ?year= makes each model year of the maintenance schedule its own crawlable,
  // indexable URL — a dropdown alone is invisible to a crawler.
  validateSearch: (search: Record<string, unknown>): { year?: number } => {
    const y = Number(search["year"]);
    return Number.isInteger(y) && y >= 1980 && y <= 2100 ? { year: y } : {};
  },
  loader: async ({ params, location }) => {
    const alias = aliasedModelSlug(params.model);
    if (alias) {
      throw redirect({
        to: "/$make/$model/$dataType",
        params: { make: params.make, model: alias, dataType: params.dataType },
        statusCode: 301,
      });
    }
    if (!isValidDataType(params.dataType)) {
      throw notFound();
    }
    const [result, related, specResult, provenByData, siblingAttr, highDemandModel] = await Promise.all([
      getVehiclesForPage({
        data: { makeSlug: params.make, modelSlug: params.model, dataType: params.dataType },
      }),
      getRelatedLinks({ data: { makeSlug: params.make, modelSlug: params.model } }),
      getSpecsForModel({
        data: {
          makeSlug: params.make,
          modelSlug: params.model,
          specType: params.dataType,
        },
      }),
      getIsProvenPage({
        data: { path: `/${params.make}/${params.model}/${params.dataType}` },
      }),
      getAttributeSiblings({
        data: { makeSlug: params.make, dataType: params.dataType },
      }),
      getIsHighDemandModel({
        data: { makeSlug: params.make, modelSlug: params.model },
      }),
    ]);
    if (!result.make || !result.model) {
      throw notFound();
    }
    // ?year= is UI state, not a document. Every year-qualified URL permanently
    // collapses into the base page; the year selector runs client-side after
    // arrival, so Google only ever sees one canonical maintenance URL.
    const worthyYears: number[] = [];
    const requestedYear = (location?.search as { year?: number } | undefined)?.year;
    if (typeof requestedYear === "number") {
      throw redirect({
        to: "/$make/$model/$dataType",
        params: { make: params.make, model: params.model, dataType: params.dataType },
        search: {},
        statusCode: 301,
      });
    }

    const extras = await getNhtsaExtrasForModel({ data: { modelId: result.model.id } });
    const evVehicles = result.vehicles.filter((v) => v.ev_range_miles != null);

    // Rebuilt curb-weight pages read two extra, page-specific inputs: the
    // manufacturer towing / fuel-tank rows (which live under their own spec
    // types) and this model's position inside its EPA size class.
    const rebuiltCurbWeight = isRebuiltCurbWeightPage(
      params.make,
      params.model,
      params.dataType,
    );
    let curbWeightSpecs: RebuildSpec[] = [];
    let classPosition: ClassPosition | null = null;
    if (rebuiltCurbWeight) {
      const [tow, tank, position] = await Promise.all([
        getSpecsForModel({
          data: { makeSlug: params.make, modelSlug: params.model, specType: "towing-capacity" },
        }),
        getSpecsForModel({
          data: { makeSlug: params.make, modelSlug: params.model, specType: "fuel-tank-capacity" },
        }),
        getCurbWeightClassPosition({
          data: { makeSlug: params.make, modelSlug: params.model },
        }),
      ]);
      classPosition = position;
      curbWeightSpecs = [...tow.specs, ...tank.specs].map((s) => ({
        year: s.year,
        spec_type: s.spec_type,
        towing_capacity_lbs: s.towing_capacity_lbs,
        towing_config: s.towing_config,
        payload_capacity_lbs: s.payload_capacity_lbs,
        gcwr_lbs: s.gcwr_lbs,
        fuel_tank_gal: s.fuel_tank_gal,
      }));
    }
    // Money pages only exist where verified manufacturer data is present —
    // never publish an empty towing / tire / bolt-pattern page.
    if (isSpecDataType(params.dataType) && specResult.specs.length === 0) {
      throw notFound();
    }
    // Same rule for the NHTSA- and EV-backed pages.
    if (params.dataType === "dimensions" && extras.dimensions.length === 0) throw notFound();
    if (
      params.dataType === "curb-weight" &&
      extras.dimensions.filter((d) => d.curb_weight_lbs != null).length === 0
    )
      throw notFound();
    if (params.dataType === "safety-ratings" && extras.safety.length === 0) throw notFound();
    if (params.dataType === EV_DATA_TYPE && evVehicles.length === 0) throw notFound();
    // EPA-derived templates (heavy-duty trucks over 8,500 lbs GVWR are not EPA
    // rated) rendered an empty table and "— MPG" answers, which is a thin page.
    const EPA_ONLY_TYPES = [
      "mpg",
      "fuel-cost",
      "engine",
      "transmission",
      "drivetrain",
      "vehicle-class",
    ];
    if (EPA_ONLY_TYPES.includes(params.dataType) && result.vehicles.length === 0) {
      throw notFound();
    }

    // Off-topic row sets (e.g. NHTSA dimensions on an MPG page) are only read
    // for counts, chips and one or two derived figures, so ship a narrow
    // projection instead of every column — it keeps the document light.
    const dimensionsActive = params.dataType === "dimensions" || params.dataType === "curb-weight";
    const dims = dimensionsActive
      ? compactRows(extras.dimensions as unknown as Record<string, unknown>[])
      : capRows(
          compactRows(
            (extras.dimensions as unknown as Record<string, unknown>[]).map((d) =>
              pickFields(d, [
                "id",
                "year",
                "length_in",
                "width_in",
                "height_in",
                "wheelbase_in",
                "curb_weight_lbs",
              ]),
            ),
          ),
          12,
        );

    // The EPA row set backs the MPG-family tables in full, but on a spec or
    // NHTSA page it only feeds chips, counts and a few derived sentences — so
    // ship the identity/engine columns and drop the cost and emissions ones.
    const epaRows = result.vehicles as unknown as Record<string, unknown>[];
    const epaFull =
      params.dataType === "mpg" ||
      params.dataType === "fuel-cost" ||
      params.dataType === "engine" ||
      params.dataType === "vehicle-class" ||
      params.dataType === "transmission" ||
      params.dataType === "drivetrain";
    // Older EPA rows on an off-topic page are only counted and charted, never
    // read column-by-column, so keep the descriptive columns for the latest
    // model year only and ship the rest as bare year/economy rows.
    const epaLatestYear = Math.max(0, ...epaRows.map((v) => Number(v["year"]) || 0));
    const epaDetailed = epaFull;
    // Maintenance intervals are derived from engine/drivetrain identity across
    // every year, so that page keeps the descriptive columns on all rows.
    const epaKeepDescriptive = params.dataType === "maintenance-schedule";
    const compactVehicles = compactRows(
      epaDetailed
        ? epaRows
        : epaRows.map((v) =>
            Number(v["year"]) !== epaLatestYear && !epaKeepDescriptive
              ? pickFields(v, [
                  "id",
                  "year",
                  "vehicle_class",
                  "atv_type",
                  "mpg_combined",
                  "ev_range_miles",
                  ...PROVENANCE_KEYS,
                ])
              :
            pickFields(v, [
              "id",
              "year",
              "trim",
              "transmission",
              "drive",
              "cylinders",
              "displacement",
              "engine_desc",
              "fuel_type",
              "vehicle_class",
              "atv_type",
              "phev_blended",
              "mpg_city",
              "mpg_hwy",
              "mpg_combined",
              "ev_range_miles",
              ...(params.dataType === EV_DATA_TYPE
                ? ["ev_range_city", "ev_range_hwy", "ev_motor", "charge_120v_hours", "charge_240v_hours"]
                : []),
              ...PROVENANCE_KEYS,
            ]),
          ),
    );
    // On a page whose own table is not the EPA table, the remaining rows only
    // feed counts and derived sentences — keep a recent window instead of the
    // whole history so the hydration payload stays small.
    // Maintenance is the exception: its year selector and year-by-year table are
    // the only place the ?year= URLs are linked from, so a flat 24-row window
    // silently dropped every older model year (and left those URLs with a single
    // inbound link). Keep a few representative rows for EVERY year instead.
    const cappedVehicles = epaDetailed
      ? capRows(compactVehicles, 150)
      : params.dataType === EV_DATA_TYPE
        ? capRows(
            compactVehicles.filter((v) => v["ev_range_miles"] != null),
            60,
          )
        : params.dataType === "maintenance-schedule"
          ? capRowsPerYear(compactVehicles, 4, 160)
          : capRows(compactVehicles, 24);


    const compactSpecs = compactRows(specResult.specs as unknown as Record<string, unknown>[]);
    const compactSafety = compactRows(extras.safety as unknown as Record<string, unknown>[]);
    // Attribution is identical across a dataset's rows, so resolve the row it
    // should come from once here and ship a single provenance object instead of
    // repeating four columns on every row (see src/lib/payload.ts).
    const latestSpecYear = compactSpecs[0]?.["year"];
    const provenance: Provenance = takeProvenance(
      isSpecDataType(params.dataType)
        ? compactSpecs.find((s) => s["year"] === latestSpecYear) ?? compactSpecs[0]
        : params.dataType === "dimensions"
          ? dims[0]
          : params.dataType === "curb-weight"
            ? dims.find((d) => d["curb_weight_lbs"] != null) ?? dims[0]
            : params.dataType === "safety-ratings"
              ? compactSafety.find((r) => r["overall_rating"]) ?? compactSafety[0]
              : params.dataType === EV_DATA_TYPE
                ? compactVehicles.find((v) => v["ev_range_miles"] != null) ?? compactVehicles[0]
                : compactVehicles[0],
    );
    return {
      ...result,
      // The only authoritative list of ?year= URLs that do not 301 — computed
      // above from the full vehicle rows, so on-page links can never point at
      // a year the loader would redirect away.
      worthyYears,
      // Duplicate EPA trim rows and null columns are stripped before the data
      // reaches the hydration payload — see src/lib/payload.ts.
      vehicles: shortIds(stripProvenance(cappedVehicles), "v") as unknown as typeof result.vehicles,
      related,
      specs: shortIds(stripProvenance(compactSpecs), "s") as unknown as typeof specResult.specs,
      availableSpecTypes: specResult.availableSpecTypes,
      dimensions: shortIds(stripProvenance(dims), "d") as unknown as typeof extras.dimensions,
      safety: shortIds(stripProvenance(compactSafety), "n") as unknown as typeof extras.safety,
      provenance,
      rebuiltCurbWeight,
      curbWeightSpecs,
      classPosition,
      provenByData,
      highDemandModel,
      attributeSiblings: siblingAttr.siblings.filter((s) => s.slug !== params.model),
      crossMakeSiblings: pickCrossMakeSiblings(
        siblingAttr.crossMake,
        `${params.make}/${params.model}/${params.dataType}`,
        // One chip per brand, no window: capping the brand list meant niche
        // brands fell outside it on most pages and stayed link-starved.
        Number.MAX_SAFE_INTEGER,
      ),
    };
  },
  component: DataTypePage,
  head: (headCtx) => {
    const { loaderData, params } = headCtx;
    // ?year= never reaches head(): the loader 301s every year-qualified URL to
    // the base page, so this template only ever describes the canonical URL.
    const searchYear: number | undefined = undefined;
    // loaderData is undefined when notFound() was thrown
    if (!loaderData?.make || !loaderData?.model) {
      return {
        meta: [{ title: "Not found — AutoSpecData" }, { name: "robots", content: "noindex" }],
      };
    }
    const { make, model } = loaderData;
    const info = getDataTypeInfo(params.dataType as DataTypeKey);
    const fullName = `${make.name} ${model.name}`;
    // Title is assembled below, after the loader rows are in scope, so it can
    // carry the real year + measured value instead of a static noun phrase.
    const path = `https://autospecdata.com/${params.make}/${params.model}/${params.dataType}`;
    const vehicles = (loaderData.vehicles ?? []) as VehicleRow[];
    const specs = (loaderData.specs ?? []) as SpecRow[];
    const isSpec = isSpecDataType(params.dataType);
    const dimensions = (loaderData.dimensions ?? []) as DimensionRow[];
    const safety = (loaderData.safety ?? []) as SafetyRow[];
    const years = isSpec ? specs.map((s) => s.year) : vehicles.map((v) => v.year);
    const latestYear = isSpec ? (specs[0]?.year ?? null) : (vehicles[0]?.year ?? null);
    const quickAnswer = buildQuickAnswer(params.dataType as DataTypeKey, {
      fullName,
      latestYear,
      vehicles,
      specs,
      dimensions,
      safety,
      makeSlug: params.make,
      makeName: make.name,
    });
    // Prefer the real, data-backed answer as the snippet — answer engines and
    // search snippets both reward a description that already answers the query.
    // Lead the snippet with the exact "<make> <model> <attribute>" phrase so the
    // head keyword is present in the description as well as the title.
    const keyPhrase = `${fullName} ${info.shortLabel}`;
    const rawDescription = quickAnswer
      ? (quickAnswer.length > 155
          ? quickAnswer
          : `${quickAnswer} See the full year-by-year table.`)
      : info.description(make.name, model.name);
    // Lower-casing the first character blindly produced "nHTSA" and "ePA" in
    // live snippets. Acronyms and any capitalised proper noun keep their case.
    const firstWord = rawDescription.split(/\s/)[0] ?? "";
    const keepsCase = /^[A-Z]{2,}/.test(firstWord) || /[A-Z]/.test(firstWord.slice(1));
    const withKeyPhrase = rawDescription.toLowerCase().includes(keyPhrase.toLowerCase())
      ? rawDescription
      : `${keyPhrase}: ${keepsCase ? rawDescription : `${rawDescription.charAt(0).toLowerCase()}${rawDescription.slice(1)}`}`;
    // Question-form lead where GSC shows the query is typed as a question
    // ("how much does X weigh") — the snippet then mirrors the search phrasing.
    const questionLead = specQuestionLead(params.dataType as DataTypeKey, { fullName, latestYear, safety, dimensions });
    const description = questionLead ? `${questionLead} ${withKeyPhrase}` : withKeyPhrase;
    const title = pageTitle(
      buildSpecTitleBase(params.dataType as DataTypeKey, {
        fullName,
        latestYear,
        vehicles,
        specs,
        dimensions,
        safety,
      }),
    );

    // Rebuilt curb-weight pages describe themselves from their own module set,
    // so the head reuses the exact answer sentence and FAQ rendered on the page.
    const rebuiltPage =
      loaderData?.rebuiltCurbWeight === true
        ? buildCurbWeightPage({
            makeName: make.name,
            modelName: model.name,
            fullName,
            dimensions: dimensions as unknown as RebuildDim[],
            vehicles: vehicles as unknown as RebuildVehicle[],
            specs: (loaderData.curbWeightSpecs ?? []) as RebuildSpec[],
            classPosition: (loaderData.classPosition ?? null) as ClassPosition | null,
            fmt: (lbs: number) => `${Math.round(lbs).toLocaleString("en-US")} lb`,
          })
        : null;

    const bestMpg = vehicles.reduce<number | null>(
      (b, v) => (v.mpg_combined && (!b || v.mpg_combined > b) ? v.mpg_combined : b),
      null,
    );
    // Auto-generated attribute pages get a strict row-count floor on the data
    // that actually backs them; everything else keeps the model-level rule.
    const attributeRowCount = ATTRIBUTE_ROW_COUNTS[params.dataType]?.({
      dimensions,
      safety,
      vehicles,
    });
    const provenByData =
      loaderData?.provenByData === true ||
      isProvenPerformerPath(`/${params.make}/${params.model}/${params.dataType}`);
    const indexable =
      attributeRowCount != null
        ? isIndexableAttributePage({
            makeSlug: params.make,
            modelSlug: params.model,
            dataType: params.dataType,
            rowCount: attributeRowCount,
            latestYear: attributeLatestYear(params.dataType, { dimensions, safety, vehicles }),
            provenByData,
            highDemandModel: loaderData?.highDemandModel === true,
            differentiators: attributeDifferentiators(params.dataType as DataTypeKey, {
              vehicles,
              specs,
              dimensions,
              safety,
              makeSlug: params.make,
              makeName: make.name,
            }),
          })
        : provenByData ||
          ((isIndexableDataType(params.dataType) ||
            loaderData?.highDemandModel === true) &&
            isIndexableModel({
              makeSlug: params.make,
              modelSlug: params.model,
              latestYear: years.length ? Math.max(...years) : latestYear,
              rowCount: isSpec ? specs.length : vehicles.length,
            }));
    // A year-qualified URL is its own page ONLY when that model year's schedule
    // actually differs from the year before it. The newest year is what the bare
    // URL already renders, and a year that repeats its predecessor's intervals is
    // byte-identical content — both canonicalise back to the base URL.
    const yearQualified =
      params.dataType === "maintenance-schedule" &&
      typeof searchYear === "number" &&
      searchYear !== latestYear &&
      buildMaintenanceYearRows(
        vehicles,
        maintenanceContext({ specs, dimensions, makeSlug: params.make, makeName: make.name }),
      ).some((r: MaintenanceYearRow) => r.year === searchYear && r.changeNote !== null);


    // Any ?year= variant gets a year-stamped title so two URLs never ship the
    // same <title>. Only genuinely distinct years also get their own canonical.
    const yearStamped = params.dataType === "maintenance-schedule" && typeof searchYear === "number";

    let finalTitle = rebuiltPage
      ? pageTitle(`${fullName} Curb Weight: ${rebuiltPage.shortAnswer}`)
      : yearStamped
      ? pageTitle(
          buildSpecTitleBase(
            params.dataType as DataTypeKey,
            { fullName, latestYear, vehicles, specs, dimensions, safety },
            searchYear,
          ),
        )
      : title;

    // The <h1> renders "<make> <model> <label>". When a page has no measured
    // value to put in the <title>, the fallback title collapses onto that same
    // string and audits flag a duplicate H1/title — stamp the model year on the
    // title instead, which is both distinct and more useful in the SERP.
    const h1Text = `${fullName} ${info.label}`;
    if (!yearStamped && finalTitle === h1Text && latestYear) {
      finalTitle = pageTitle(`${latestYear} ${h1Text}`);
    }



    const finalPath = yearQualified ? `${path}?year=${searchYear}` : path;
    // Snippets over ~160 chars get truncated by Google mid-sentence and are
    // flagged as an on-page content issue, so clamp at the last clause/word
    // boundary that fits instead of shipping a run-on description.
    const clampSnippet = (s: string, max = 158): string => {
      if (s.length <= max) return s;
      const head = s.slice(0, max);
      const clause = Math.max(head.lastIndexOf("; "), head.lastIndexOf(", "), head.lastIndexOf(". "));
      if (clause > max * 0.6) return `${head.slice(0, clause).trimEnd()}.`;
      const word = head.lastIndexOf(" ");
      return `${head.slice(0, word > 0 ? word : max).trimEnd()}…`;
    };
    const finalDescription = clampSnippet(
      rebuiltPage
        ? rebuiltPage.answer
        : yearStamped
        ? `${searchYear} ${fullName} maintenance schedule: oil change, tire rotation and fluid intervals for that model year, plus what changed versus the prior year.`
        : description,
    );
    return {
      meta: [
        { title: finalTitle },
        ...(indexable ? [] : NOINDEX_META),
        { name: "description", content: finalDescription },
        { property: "og:title", content: yearStamped ? `${searchYear} ${fullName} ${info.title}` : `${fullName} ${info.title}` },

        { property: "og:description", content: finalDescription },
        { property: "og:url", content: finalPath },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: SITE_OG_IMAGE },
        { name: "twitter:image", content: SITE_OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: finalPath }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: `${fullName} ${info.title}`,
            url: path,
            description,
            inLanguage: "en-US",
            isPartOf: { "@type": "WebSite", name: "AutoSpecData", url: "https://autospecdata.com/" },
            primaryImageOfPage: undefined,
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", "[data-quick-answer]"],
            },
            dateModified: isSpec ? (loaderData.provenance?.verified_at ?? undefined) : undefined,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Dataset",
            "@id": `${path}#dataset`,
            name: `${fullName} ${info.title}`,
            description: description,
            creator: { "@type": "Organization", "@id": "https://autospecdata.com/#organization", name: "AutoSpecData", url: "https://autospecdata.com" },
            isPartOf: { "@id": "https://autospecdata.com/#dataset" },
            isBasedOn: isSpec
              ? publicSourceUrl(loaderData.provenance?.source_url) || loaderData.provenance?.source
              : "https://fueleconomy.gov",
            license: "https://creativecommons.org/licenses/by/4.0/",
            isAccessibleForFree: true,
            dateModified: isSpec ? loaderData.provenance?.verified_at : undefined,
            keywords: [
              `${fullName} ${info.shortLabel}`,
              `${make.name} ${info.shortLabel}`,
              info.label,
            ],
            url: path,
            variableMeasured: bestMpg
              ? [
                  { "@type": "PropertyValue", name: info.shortLabel },
                  {
                    "@type": "PropertyValue",
                    name: "Best combined fuel economy",
                    value: bestMpg,
                    unitText: "MPG",
                  },
                ]
              : info.shortLabel,
            temporalCoverage:
              years.length > 0 ? `${Math.min(...years)}/${Math.max(...years)}` : undefined,
            distribution: undefined, }), }, /* vehicleLd()[1] is the Vehicle node; [0] (Dataset) is skipped to avoid duplicating the richer Dataset above */ { type: "application/ld+json", children: JSON.stringify(vehicleLd({ make: make.name, model: model.name, path, yearMin: years.length ? Math.min(...years) : null, yearMax: years.length ? Math.max(...years) : null, bestMpg, fuelType: vehicles[0]?.fuel_type ?? null, vehicleClass: vehicles[0]?.vehicle_class ?? null, transmission: vehicles[0]?.transmission ?? null, drive: vehicles[0]?.drive ?? null, description })[1]) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: make.name, path: `/${params.make}` },
              { name: model.name, path: `/${params.make}/${params.model}` },
              { name: info.shortLabel, path },
            ]),
          ),
        },
        ...(() => {
          const faqs = rebuiltPage
            ? rebuiltPage.faqs
            : buildDataTypeFaqs({
            fullName,
            dataType: params.dataType as DataTypeKey,
            vehicles,
            specs,
            dimensions,
            safety,
            latestYear,
            makeSlug: params.make,
            makeName: make.name,
          });
          return faqs.length > 0
            ? [{ type: "application/ld+json", children: JSON.stringify(faqLd(faqs)) }]
            : [];
        })(),


      ],
    };
  },
});

const KNOWN_SOURCE_URLS: Record<string, string> = {
  "fueleconomy.gov": "https://www.fueleconomy.gov/feg/ws/",
  "epa": "https://www.fueleconomy.gov/feg/ws/",
};

/** Rows that actually back each auto-generated attribute page. */
interface AttributeSources {
  dimensions: DimensionRow[];
  safety: SafetyRow[];
  vehicles: VehicleRow[];
}

const ATTRIBUTE_ROW_COUNTS: Record<string, (s: AttributeSources) => number> = {
  dimensions: (s) => s.dimensions.length,
  "curb-weight": (s) => s.dimensions.filter((d) => d.curb_weight_lbs != null).length,
  "safety-ratings": (s) => s.safety.length,
  "ev-range": (s) => s.vehicles.filter((v) => v.ev_range_miles != null).length,
};

function attributeLatestYear(dataType: string, s: AttributeSources): number | null {
  const years =
    dataType === "safety-ratings"
      ? s.safety.map((r) => r.year)
      : dataType === "ev-range"
        ? s.vehicles.filter((v) => v.ev_range_miles != null).map((v) => v.year)
        : dataType === "curb-weight"
          ? s.dimensions.filter((d) => d.curb_weight_lbs != null).map((d) => d.year)
          : s.dimensions.map((d) => d.year);
  return years.length ? Math.max(...years) : null;
}

function resolveSourceUrl(source: string | null | undefined, sourceUrl: string | null | undefined) {
  if (sourceUrl) return sourceUrl;
  if (!source) return null;
  const key = Object.keys(KNOWN_SOURCE_URLS).find((k) => source.toLowerCase().includes(k));
  return key ? KNOWN_SOURCE_URLS[key] : null;
}

/** Data types where showing an imperial/metric toggle is meaningful. */
const UNIT_TOGGLE_DATA_TYPES: DataTypeKey[] = [
  "dimensions",
  "towing-capacity",
  "mpg",
  "ev-range",
  "curb-weight",
  "bed-dimensions",
];

/**
 * Model-level facts that make one maintenance schedule differ from another:
 * how much the vehicle tows, how heavy it is, and whose service practice it
 * follows. Without these every model shares one of a handful of schedules.
 */
export function maintenanceContext({
  specs,
  dimensions,
  makeSlug,
  makeName,
}: {
  specs: SpecRow[];
  dimensions: DimensionRow[];
  makeSlug?: string | null | undefined;
  makeName?: string | null | undefined;
}): MaintenanceContext {
  const towingLbs = specs.reduce<number | null>(
    (b, s) => (s.towing_capacity_lbs && (!b || s.towing_capacity_lbs > b) ? s.towing_capacity_lbs : b),
    null,
  );
  const curbWeightLbs = dimensions.reduce<number | null>(
    (b, d) => (d.curb_weight_lbs && (!b || d.curb_weight_lbs > b) ? Math.round(d.curb_weight_lbs) : b),
    null,
  );
  return { makeSlug: makeSlug ?? null, makeName: makeName ?? null, towingLbs, curbWeightLbs };
}

/**
 * How many facts on this page come from this vehicle's own data rather than
 * the shared template. Feeds the differentiation floor in index-policy.
 */
export function attributeDifferentiators(
  dataType: DataTypeKey,
  input: {
    vehicles: any[];
    specs: SpecRow[];
    dimensions: DimensionRow[];
    safety: SafetyRow[];
    makeSlug?: string;
    makeName?: string;
  },
): number {
  const { vehicles, specs, dimensions, safety } = input;
  if (dataType === "maintenance-schedule") {
    const ctx = maintenanceContext({
      specs,
      dimensions,
      makeSlug: input.makeSlug,
      makeName: input.makeName,
    });
    const p = buildMaintenanceProfile(vehicles, ctx);
    return countDifferentiators([
      ctx.towingLbs,
      ctx.curbWeightLbs,
      p.oilIntervalMiles !== 7500 ? p.oilIntervalMiles : null,
      p.powertrain,
      p.items.length >= 8 ? p.items.length : null,
      vehicles[0]?.displacement ?? null,
      vehicles[0]?.transmission ?? null,
      vehicles[0]?.drive ?? null,
    ]);
  }
  if (dataType === EV_DATA_TYPE) {
    // The EV-range page is built from the electric columns, but the generic
    // scorer below reads engine columns (displacement, cylinders, transmission)
    // that the loader deliberately strips from this page's projection. That
    // mismatch scored real, data-rich EV pages at 1 differentiator and pushed
    // them under the floor, so score the facts the page actually renders.
    const ev = vehicles.filter((v) => v?.ev_range_miles != null);
    const e = ev[0];
    return countDifferentiators([
      e?.ev_range_miles ?? null,
      e?.ev_range_city ?? null,
      e?.ev_range_hwy ?? null,
      e?.ev_motor ?? null,
      e?.charge_120v_hours ?? null,
      e?.charge_240v_hours ?? null,
      e?.mpg_combined ?? null,
      e?.trim ?? null,
      // A multi-year electric-range history is itself model-specific: it shows
      // how this nameplate's range moved between model years.
      ev.length > 1 ? ev.length : null,
      // Range spread across trims/years only exists when the figures differ.
      new Set(ev.map((v) => v.ev_range_miles)).size > 1 ? "range-spread" : null,
    ]);
  }
  if (dataType === "safety-ratings") {

    const r = safety[0];
    return countDifferentiators([
      r?.overall_rating ?? null,
      r?.overall_front_rating ?? null,
      r?.overall_side_rating ?? null,
      r?.rollover_rating ?? null,
      safety.length > 1 ? safety.length : null,
      // NHTSA leaves crash-star fields empty for models it never barrier-tested
      // (most low-volume performance trims). Those pages still carry unique,
      // model-specific NHTSA data: rollover risk percentage, recall /
      // complaint / investigation counts and driver-assist availability.
      r?.rollover_possibility ?? null,
      r?.recalls_count ? r.recalls_count : null,
      r?.nhtsa_complaints_count ? r.nhtsa_complaints_count : null,
      r?.investigation_count ? r.investigation_count : null,
      r?.has_forward_collision_warning ?? null,
      r?.has_lane_departure_warning ?? null,
    ]);
  }
  if (dataType === "dimensions" || dataType === "curb-weight") {
    const d = dimensions[0];
    return countDifferentiators([
      d?.length_in ?? null,
      d?.width_in ?? null,
      d?.height_in ?? null,
      d?.wheelbase_in ?? null,
      d?.curb_weight_lbs ?? null,
      dimensions.length > 1 ? dimensions.length : null,
    ]);
  }
  if (specs.length > 0) {
    const s = specs[0] as any;
    return countDifferentiators([
      s?.towing_capacity_lbs ?? null,
      s?.payload_lbs ?? null,
      s?.value_text ?? null,
      s?.value_numeric ?? null,
      specs.length > 2 ? specs.length : null,
      vehicles[0]?.trim ?? null,
    ]);
  }
  return countDifferentiators([
    vehicles[0]?.mpg_combined ?? null,
    vehicles[0]?.displacement ?? null,
    vehicles[0]?.cylinders ?? null,
    vehicles[0]?.transmission ?? null,
    vehicles[0]?.drive ?? null,
    vehicles.length > 3 ? vehicles.length : null,
  ]);
}

function DataTypePage() {
  const { year: selectedYear } = Route.useSearch();
  const {
    make,
    model,
    vehicles,
    related,
    specs,
    availableSpecTypes,
    dimensions,
    safety,
    provenance,
    attributeSiblings,
    crossMakeSiblings,
    worthyYears,
    rebuiltCurbWeight,
    curbWeightSpecs,
    classPosition,
  } = Route.useLoaderData();

  const params = Route.useParams();
  const dataType = params.dataType as DataTypeKey;
  const info = getDataTypeInfo(dataType);
  const fullName = `${make.name} ${model.name}`;
  // ?year= scopes the maintenance page to one model year, so H1 and the direct
  // answer match the title/canonical a crawler sees for that URL.
  const yearScoped =
    dataType === "maintenance-schedule" &&
    typeof selectedYear === "number" &&
    vehicles.some((v) => v.year === selectedYear);
  const isSpec = isSpecDataType(dataType);
  // Page model for the rebuilt curb-weight layout (null on every other page).
  const rebuiltPageModel = useCurbWeightPage({
    makeName: make.name,
    modelName: model.name,
    fullName,
    dimensions: (rebuiltCurbWeight ? dimensions : []) as unknown as RebuildDim[],
    vehicles: vehicles as unknown as RebuildVehicle[],
    specs: (curbWeightSpecs ?? []) as RebuildSpec[],
    classPosition: (classPosition ?? null) as ClassPosition | null,
  });
  const rebuilt = rebuiltCurbWeight ? rebuiltPageModel : null;
  // A vehicle with no recorded weights produces no module set, so those pages
  // keep the previous rendering rather than losing their body copy.
  const useRebuilt = rebuilt !== null;

  // Get the most recent year's data for the direct answer
  const latestYear = isSpec ? (specs[0]?.year ?? null) : (vehicles[0]?.year ?? null);
  const latestVehicles = vehicles.filter((v: VehicleRow) => v.year === latestYear);
  const latestSpecs = specs.filter((s: SpecRow) => s.year === latestYear);
  const quickAnswer = buildQuickAnswer(dataType, {
    fullName,
    latestYear,
    vehicles,
    specs,
    dimensions,
    safety,
    makeSlug: make.slug,
    makeName: make.name,
  });
  const evVehicles = vehicles.filter((v: VehicleRow) => v.ev_range_miles != null);
  const availableExtras: DataTypeKey[] = [
    ...(dimensions.length ? (["dimensions"] as DataTypeKey[]) : []),
    ...(dimensions.some((d: DimensionRow) => d.curb_weight_lbs != null)
      ? (["curb-weight"] as DataTypeKey[])
      : []),
    ...(safety.length ? (["safety-ratings"] as DataTypeKey[]) : []),
    ...(evVehicles.length ? (["ev-range"] as DataTypeKey[]) : []),
  ];
  const [unit] = useUnitSystem();

  // Attribution is resolved server-side and shipped once — see src/lib/payload.ts.
  const badgeRow = provenance;
  const showUnitToggle = UNIT_TOGGLE_DATA_TYPES.includes(dataType);

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mx-auto max-w-6xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/$make" params={{ make: make.slug }} className="hover:text-foreground">{make.name}</Link>
        <span className="mx-2">/</span>
        <Link to="/$make/$model" params={{ make: make.slug, model: model.slug }} className="hover:text-foreground">{model.name}</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{info.shortLabel}</span>
      </nav>

      {/* Header with direct answer */}
      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
          {/* The H1 carries the exact "<make> <model> <attribute>" query phrase
              using the short label, so it stays distinct from the longer
              <title>; the question-style headline stays on the page as the
              sub-heading so the answer-engine framing is preserved. */}
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
            {rebuilt
              ? rebuilt.h1
              : `${yearScoped ? `${selectedYear} ` : ""}${fullName} ${info.label}`}
          </h1>
          {rebuilt ? (
            <p
              data-quick-answer
              className="mt-4 max-w-3xl text-base sm:text-lg leading-relaxed text-foreground"
            >
              {rebuilt.answer}
            </p>
          ) : null}
          {(() => {
            if (rebuilt) return null;
            const question = specHeadline(dataType, { fullName, latestYear, vehicles, specs });
            return question ? (
              <h2 className="mt-2 text-lg sm:text-xl font-semibold text-muted-foreground">
                {question}
              </h2>
            ) : null;
          })()}
          {(() => {
            if (rebuilt) return null;
            const bluf = specBluf(dataType, { fullName, latestYear, vehicles, specs });
            // Maintenance pages must not fall back to the generic MPG/towing
            // summary — that boilerplate is off-intent for service content.
            const text =
              bluf ??
              (dataType === "maintenance-schedule"
                ? null
                : buildBlufSummary({
                    makeName: make.name,
                    modelName: model.name,
                    vehicles,
                    specs,
                  }));
            return text ? <BlufSummary text={text} /> : null;
          })()}


          {!rebuilt && quickAnswer && (
            <p
              data-quick-answer
              className="mt-4 max-w-3xl text-base sm:text-lg leading-relaxed text-foreground"
            >
              {quickAnswer}
            </p>
          )}

          {/* De-cannibalization: weight queries belong to /curb-weight, not
              /dimensions. Both URLs were surfacing for "how much does a X weigh". */}
          {dataType === "dimensions" && (
            <p className="mt-3 max-w-3xl text-sm text-muted-foreground">
              Looking for weight? See the{" "}
              <Link
                to="/$make/$model/$dataType"
                params={{ make: make.slug, model: model.slug, dataType: "curb-weight" }}
                className="text-primary underline"
              >
                {fullName} curb weight
              </Link>{" "}
              page — this page covers length, width, height and wheelbase.
            </p>
          )}


          {/* Direct answer above the fold */}
          {latestYear && (
            <div className="mt-6 rounded-lg border border-border bg-card p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Latest model year ({latestYear}):
                  </p>
                  {dataType === "mpg" && <MpgDirectAnswer vehicles={latestVehicles} fullName={fullName} />}
                  {dataType === "engine" && <EngineDirectAnswer vehicles={latestVehicles} fullName={fullName} />}
                  {dataType === "fuel-cost" && <FuelCostDirectAnswer vehicles={latestVehicles} fullName={fullName} />}
                  {dataType === "transmission" && <TransmissionDirectAnswer vehicles={latestVehicles} fullName={fullName} />}
                  {dataType === "drivetrain" && <DrivetrainDirectAnswer vehicles={latestVehicles} fullName={fullName} />}
                  {dataType === "vehicle-class" && <VehicleClassDirectAnswer vehicles={latestVehicles} fullName={fullName} />}
                  {dataType === "maintenance-schedule" && (
                    <MaintenanceDirectAnswer
                      vehicles={
                        yearScoped
                          ? vehicles.filter((v) => v.year === selectedYear)
                          : latestVehicles.length
                            ? latestVehicles
                            : vehicles
                      }
                      fullName={yearScoped ? `${selectedYear} ${fullName}` : fullName}
                      ctx={maintenanceContext({ specs, dimensions, makeSlug: make.slug, makeName: make.name })}
                    />
                  )}
                  {dataType === "towing-capacity" && <TowingDirectAnswer specs={latestSpecs} fullName={fullName} />}
                  {dataType === "tire-size" && <TireDirectAnswer specs={latestSpecs} fullName={fullName} />}
                  {dataType === "bolt-pattern" && <BoltPatternDirectAnswer specs={latestSpecs} fullName={fullName} />}
                  {dataType === "bed-dimensions" && <BedDirectAnswer specs={latestSpecs} fullName={fullName} />}
                  {dataType === "fuel-tank-capacity" && <FuelTankDirectAnswer specs={latestSpecs} vehicles={latestVehicles.length ? latestVehicles : vehicles} fullName={fullName} />}
                  {dataType === "dimensions" && <DimensionsDirectAnswer dimensions={dimensions} fullName={fullName} />}
                  {dataType === "curb-weight" &&
                    (rebuilt ? (
                      <p className="text-2xl font-bold text-foreground">
                        {rebuilt.shortAnswer.replace(/\s*\(\d{4}\)$/, "")}
                      </p>
                    ) : (
                      <CurbWeightDirectAnswer dimensions={dimensions} fullName={fullName} />
                    ))}
                  {dataType === "safety-ratings" && <SafetyDirectAnswer safety={safety} fullName={fullName} />}
                  {dataType === "ev-range" && <EvDirectAnswer vehicles={evVehicles} fullName={fullName} />}
                </div>
                <VerifiedBadge
                  source={badgeRow?.source}
                  sourceUrl={resolveSourceUrl(badgeRow?.source, badgeRow?.source_url)}
                  verifiedAt={badgeRow?.verified_at}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Cross-links */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-4">
        <div className="flex gap-2 flex-wrap text-sm">
          <Link
            to="/$make/$model"
            params={{ make: make.slug, model: model.slug }}
            className="px-3 py-1 rounded-full border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors"
          >
            Overview
          </Link>
          {ALL_DATA_TYPES.filter(
            (dt) => vehicles.length > 0 || dt === "maintenance-schedule",
          ).map((dt) => {
            const dtInfo = getDataTypeInfo(dt);
            const isActive = dt === dataType;
            return (
              <Link
                key={dt}
                to="/$make/$model/$dataType"
                params={{ make: make.slug, model: model.slug, dataType: dtInfo.slug }}
                className={
                  isActive
                    ? "px-3 py-1 rounded-full bg-primary text-primary-foreground font-medium"
                    : "px-3 py-1 rounded-full border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                }
              >
                {dtInfo.shortLabel}
              </Link>
            );
          })}
          {SPEC_DATA_TYPES.filter((dt) => availableSpecTypes.includes(dt)).map((dt) => {
            const dtInfo = getDataTypeInfo(dt);
            const isActive = dt === dataType;
            return (
              <Link
                key={dt}
                to="/$make/$model/$dataType"
                params={{ make: make.slug, model: model.slug, dataType: dtInfo.slug }}
                className={
                  isActive
                    ? "px-3 py-1 rounded-full bg-primary text-primary-foreground font-medium"
                    : "px-3 py-1 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                }
              >
                {dtInfo.shortLabel}
              </Link>
            );
          })}
          {availableExtras.map((dt) => {
            const dtInfo = getDataTypeInfo(dt);
            const isActive = dt === dataType;
            return (
              <Link
                key={dt}
                to="/$make/$model/$dataType"
                params={{ make: make.slug, model: model.slug, dataType: dtInfo.slug }}
                className={
                  isActive
                    ? "px-3 py-1 rounded-full bg-primary text-primary-foreground font-medium"
                    : "px-3 py-1 rounded-full border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                }
              >
                {dtInfo.shortLabel}
              </Link>
            );
          })}
        </div>
      </section>

      {/* Spec table */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <h2 className="text-lg font-semibold text-foreground">
            {fullName} {info.shortLabel} — All Years
          </h2>
          {dataType === "maintenance-schedule" && <EstimatedBadge />}
          {showUnitToggle && <UnitToggle />}
        </div>
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          {dataType === "mpg" && <MpgTable vehicles={vehicles} />}
          {dataType === "engine" && <EngineTable vehicles={vehicles} />}
          {dataType === "fuel-cost" && <FuelCostTable vehicles={vehicles} />}
          {dataType === "transmission" && <TransmissionTable vehicles={vehicles} />}
          {dataType === "drivetrain" && <DrivetrainTable vehicles={vehicles} />}
          {dataType === "vehicle-class" && <VehicleClassTable vehicles={vehicles} />}
          {dataType === "maintenance-schedule" && (
            <>
              <MaintenanceSchedule
                vehicles={vehicles}
                fullName={fullName}
                makeSlug={make.slug}
                modelSlug={model.slug}
                availableSpecTypes={availableSpecTypes}
                ctx={maintenanceContext({ specs, dimensions, makeSlug: make.slug, makeName: make.name })}
                byYear
                linkableYears={worthyYears}
                year={selectedYear ?? null}
              />
              {maintenanceSpecModelUrl(make.slug, model.slug) && (
                <div className="mt-6">
                  <MaintenanceSpecLink
                    heading="Manufacturer service schedule"
                    body={`Looking for the manufacturer-based service schedule for the ${fullName}?`}
                    anchor={`View the ${model.name} maintenance schedule on MaintenanceSpec`}
                    href={maintenanceSpecModelUrl(make.slug, model.slug)!}
                  />
                </div>
              )}
            </>
          )}

          {dataType === "towing-capacity" && <TowingTable specs={specs} />}
          {dataType === "tire-size" && <TireTable specs={specs} />}
          {dataType === "bolt-pattern" && <BoltPatternTable specs={specs} />}
          {dataType === "bed-dimensions" && <BedTable specs={specs} />}
          {dataType === "fuel-tank-capacity" && <FuelTankTable specs={specs} vehicles={vehicles} />}
          {dataType === "dimensions" && <DimensionsTable dimensions={dimensions} />}
          {dataType === "curb-weight" && <CurbWeightTable dimensions={dimensions} />}
          {dataType === "safety-ratings" && <SafetyTable safety={safety} />}
          {dataType === "ev-range" && <EvTable vehicles={evVehicles} />}
        </div>
        {isSpec ? (
          <p className="text-xs text-muted-foreground mt-3">
            Manufacturer specification data
            {provenance?.source ? ` sourced from ${provenance.source}` : ""}, last verified{" "}
            {provenance?.verified_at
              ? new Date(provenance.verified_at).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : "recently"}
            . Always confirm against the owner's manual or door-jamb placard before
            towing or buying tires — figures vary by cab, bed, axle ratio, and options.
          </p>
        ) : dataType === "maintenance-schedule" ? (
          <p className="text-xs text-muted-foreground mt-3">
            Schedule generated from this vehicle's powertrain, transmission, and
            drivetrain (fueleconomy.gov / U.S. Department of Energy) using mainstream
            manufacturer service practice for its model year. Your owner's manual is the
            authority — follow its severe-duty schedule if you tow, idle a lot, or drive
            mostly short trips.
          </p>
        ) : isNhtsaDataType(dataType) ? (
          <p className="text-xs text-muted-foreground mt-3">
            {dataType === "dimensions"
              ? "Dimensions sourced from the NHTSA Canadian Vehicle Specifications database. Figures are measured per body style and can vary slightly by trim, wheel package, and suspension."
              : dataType === "curb-weight"
                ? "Curb weights sourced from the NHTSA Canadian Vehicle Specifications database and recorded per body style, with fluids and a full fuel tank but no occupants or cargo."
                : "Crash-test star ratings sourced from the NHTSA New Car Assessment Program (NCAP). Ratings apply to the tested body style and can differ between trims and drivetrain layouts."}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground mt-3">
            Data sourced from fueleconomy.gov (U.S. Department of Energy). MPG figures
            are EPA-tested. Fuel costs are estimates based on average fuel prices and
            15,000 miles/year.
          </p>
        )}
      </section>

      {/* Proprietary benchmarks */}
      {dataType === "curb-weight" &&
        (useRebuilt ? (
          <CurbWeightRebuild
            makeName={make.name}
            modelName={model.name}
            fullName={fullName}
            dimensions={dimensions as unknown as RebuildDim[]}
            vehicles={vehicles as unknown as RebuildVehicle[]}
            specs={(curbWeightSpecs ?? []) as RebuildSpec[]}
            classPosition={(classPosition ?? null) as ClassPosition | null}
          />
        ) : (
          <CurbWeightContext dimensions={dimensions} fullName={fullName} />
        ))}
      {/* Data-derived analysis — built from this model's own figures */}
      {!useRebuilt && (
      <SpecNarrative
        fullName={fullName}
        makeName={make.name}
        dataType={dataType}
        makeSlug={make.slug}
        modelSlug={model.slug}
        maintenanceCtx={maintenanceContext({ specs, dimensions, makeSlug: make.slug, makeName: make.name })}
        vehicles={vehicles}
        specs={specs}
        dimensions={dimensions}
        safety={safety}
      />
      )}
      {dataType === "dimensions" && (
        <DimensionsContext
          dimensions={dimensions}
          fullName={fullName}
          makeSlug={make.slug}
          modelSlug={model.slug}
        />
      )}
      {(dataType === "towing-capacity" || dataType === "mpg") && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-8 border-t border-border">
          <h2 className="text-xl font-bold text-foreground mb-2">
            Official Factory Specs vs. AutoSpecData Expert Benchmarks
          </h2>
          <p className="text-sm text-muted-foreground mb-4 max-w-3xl">
            {dataType === "towing-capacity"
              ? "Factory ratings are measured cold, flat, and empty. These are the numbers we would actually hook a trailer to."
              : "The EPA highway loop never holds interstate speed. Here is what the same engine does when you sit on the cruise control."}
          </p>
          {dataType === "towing-capacity" ? (
            <TowingBenchmarkTable
              fullName={fullName}
              officialLbs={specs.reduce<number | null>(
                (b: number | null, s: SpecRow) =>
                  s.towing_capacity_lbs && (!b || s.towing_capacity_lbs > b)
                    ? s.towing_capacity_lbs
                    : b,
                null,
              )}
            />
          ) : (
            <RealWorldMpgTable
              fullName={fullName}
              hwyMpg={vehicles.reduce<number | null>(
                (b: number | null, v: VehicleRow) =>
                  v.mpg_hwy && (!b || v.mpg_hwy > b) ? v.mpg_hwy : b,
                null,
              )}
              vehicleClass={vehicles[0]?.vehicle_class ?? null}
            />
          )}
        </section>
      )}

      {/* FAQ section — rebuilt curb-weight pages emit their own gated FAQ. */}
      {!useRebuilt && (
      <section className="mx-auto max-w-3xl px-4 sm:px-6 py-12 border-t border-border">
        <h3 className="text-xl font-bold text-foreground mb-4">
          Frequently Asked Questions About {model.name} {info.shortLabel}
        </h3>
        <FaqBlock
          items={buildDataTypeFaqs({
            fullName,
            dataType,
            vehicles,
            specs,
            dimensions,
            safety,
            latestYear,
            makeSlug: make.slug,
            makeName: make.name,
          })}
        />
      </section>
      )}

      <section className="mx-auto max-w-6xl px-4 sm:px-6 pb-2">
        <Link
          to="/$make/$model"
          params={{ make: make.slug, model: model.slug }}
          className="cta-gold"
        >
          View full {fullName} specs
        </Link>
      </section>

      <RelatedLinks
        makeName={make.name}
        makeSlug={make.slug}
        modelName={model.name}
        modelSlug={model.slug}
        related={related}
        dataType={dataType}
        attributeSiblings={attributeSiblings}
        crossMakeSiblings={crossMakeSiblings}
        hasEpaData={vehicles.length > 0}
        currentModelSlug={model.slug}
      />

      <UsefulTools
        makeSlug={make.slug}
        latestYear={latestYear ?? null}
        rowCount={isSpecDataType(dataType) ? specs.length : vehicles.length}
      />
    </div>
  );
}

// --- Direct answer components ---

function MpgDirectAnswer({ vehicles, fullName }: { vehicles: any[]; fullName: string }) {
  const [unit] = useUnitSystem();
  if (vehicles.length === 0) return <p>No data available.</p>;
  const best = vehicles.reduce(
    (b, v) => (v.mpg_combined && (!b || v.mpg_combined > b.mpg_combined) ? v : b),
    vehicles[0],
  );
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong> gets up to{" "}
      <strong className="text-primary text-xl">{formatMpg(best.mpg_combined, unit)} combined</strong>
      {best.mpg_city && best.mpg_hwy
        ? ` (${formatMpgValue(best.mpg_city, unit)} city / ${formatMpgValue(best.mpg_hwy, unit)} highway${unit === "metric" ? " L/100km" : ""})`
        : ""}
      {best.trim ? ` in the ${best.trim} trim` : ""}.
    </p>
  );
}

function EngineDirectAnswer({ vehicles, fullName }: { vehicles: any[]; fullName: string }) {
  if (vehicles.length === 0) return <p>No data available.</p>;
  const v = vehicles[0];
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong> is available with{" "}
      <strong className="text-primary">
        {v.cylinders || "—"}{v.displacement ? ` ${v.displacement}L` : ""}
      </strong>
      {v.transmission ? ` and ${v.transmission}` : ""}
      {v.drive ? ` (${v.drive})` : ""}.
    </p>
  );
}

function FuelCostDirectAnswer({ vehicles, fullName }: { vehicles: any[]; fullName: string }) {
  if (vehicles.length === 0) return <p>No data available.</p>;
  const cheapest = vehicles.reduce(
    (b, v) => (v.annual_fuel_cost && (!b || v.annual_fuel_cost < b.annual_fuel_cost) ? v : b),
    vehicles[0],
  );
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong> costs an estimated{" "}
      <strong className="text-primary text-xl">
        ${cheapest.annual_fuel_cost?.toLocaleString()}/year
      </strong>{" "}
      in fuel
      {cheapest.co2_tailpipe_gpm
        ? ` (${cheapest.co2_tailpipe_gpm} g CO₂/mi)`
        : ""}
      .
    </p>
  );
}

// --- Table components ---

function uniqueValues(vehicles: any[], key: string): string[] {
  return Array.from(
    new Set(vehicles.map((v) => v[key]).filter((x): x is string => Boolean(x))),
  );
}

// --- Manufacturer spec components (towing / tire / bolt pattern) ---

const lbs = (n: number | null | undefined) => (n ? `${n.toLocaleString()} lbs` : "—");

function TowingDirectAnswer({ specs, fullName }: { specs: SpecRow[]; fullName: string }) {
  const [unit] = useUnitSystem();
  if (specs.length === 0) return <p>No towing data available.</p>;
  const max = specs.reduce((b, s) =>
    (s.towing_capacity_lbs ?? 0) > (b.towing_capacity_lbs ?? 0) ? s : b,
  );
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong> can tow up to{" "}
      <strong className="text-primary text-xl">{formatWeight(max.towing_capacity_lbs, unit)}</strong>
      {max.trim ? ` in the ${max.trim}` : ""}
      {max.towing_config ? ` (${max.towing_config})` : ""}
      {max.payload_capacity_lbs
        ? `, with a payload capacity of ${formatWeight(max.payload_capacity_lbs, unit)}`
        : ""}
      .
    </p>
  );
}

function TireDirectAnswer({ specs, fullName }: { specs: SpecRow[]; fullName: string }) {
  const s = specs[0];
  if (!s) return <p>No tire data available.</p>;
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong> uses{" "}
      <strong className="text-primary text-xl">{s.front_tire_size || "—"}</strong> tires
      {s.rear_tire_size && s.rear_tire_size !== s.front_tire_size
        ? ` up front and ${s.rear_tire_size} at the rear`
        : ""}
      {s.wheel_size ? ` on ${s.wheel_size} wheels` : ""}
      {s.tire_pressure_front_psi
        ? `, inflated to ${s.tire_pressure_front_psi} PSI`
        : ""}
      .
    </p>
  );
}

function BoltPatternDirectAnswer({ specs, fullName }: { specs: SpecRow[]; fullName: string }) {
  const s = specs[0];
  if (!s) return <p>No bolt pattern data available.</p>;
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong> has a{" "}
      <strong className="text-primary text-xl">{s.bolt_pattern || "—"}</strong> bolt
      pattern
      {s.center_bore_mm ? ` with a ${s.center_bore_mm} mm center bore` : ""}
      {s.thread_size ? ` and ${s.thread_size} lug threads` : ""}
      {s.lug_nut_torque_ftlb
        ? `, torqued to ${s.lug_nut_torque_ftlb} ft-lb`
        : ""}
      .
    </p>
  );
}

function TowingTable({ specs }: { specs: SpecRow[] }) {
  const [unit] = useUnitSystem();
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Trim</th>
          <th>Configuration</th>
          <th>Max Towing</th>
          <th>Payload</th>
          <th>Curb Weight</th>
          <th>GCWR</th>
        </tr>
      </thead>
      <tbody>
        {specs.map((s, i) => (
          <tr key={s.id}>
            <td data-numeric>
              {s.year}
              <ConfigTag label={configLabels(specs)[i]} />
            </td>
            <td>{s.trim || "—"}</td>
            <td>{s.towing_config || "—"}</td>
            <td data-numeric className="font-semibold text-primary">
              {formatWeight(s.towing_capacity_lbs, unit)}
            </td>
            <td data-numeric>{formatWeight(s.payload_capacity_lbs, unit)}</td>
            <td data-numeric>{formatWeight(s.curb_weight_lbs, unit)}</td>
            <td data-numeric>{formatWeight(s.gcwr_lbs, unit)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TireTable({ specs }: { specs: SpecRow[] }) {
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Trim</th>
          <th>Front Tire</th>
          <th>Rear Tire</th>
          <th>Wheel</th>
          <th>Front PSI</th>
          <th>Rear PSI</th>
          <th>Spare</th>
        </tr>
      </thead>
      <tbody>
        {specs.map((s, i) => (
          <tr key={s.id}>
            <td data-numeric>
              {s.year}
              <ConfigTag label={configLabels(specs)[i]} />
            </td>
            <td>{s.trim || "—"}</td>
            <td className="font-semibold text-primary">{s.front_tire_size || "—"}</td>
            <td>{s.rear_tire_size || s.front_tire_size || "—"}</td>
            <td>{s.wheel_size || "—"}</td>
            <td data-numeric>{s.tire_pressure_front_psi ?? "—"}</td>
            <td data-numeric>{s.tire_pressure_rear_psi ?? "—"}</td>
            <td>{s.spare_tire_size || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BoltPatternTable({ specs }: { specs: SpecRow[] }) {
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Trim</th>
          <th>Bolt Pattern (PCD)</th>
          <th>Center Bore</th>
          <th>Thread Size</th>
          <th>Lug Torque</th>
          <th>Wheel</th>
        </tr>
      </thead>
      <tbody>
        {specs.map((s, i) => (
          <tr key={s.id}>
            <td data-numeric>
              {s.year}
              <ConfigTag label={configLabels(specs)[i]} />
            </td>
            <td>{s.trim || "—"}</td>
            <td className="font-semibold text-primary">{s.bolt_pattern || "—"}</td>
            <td data-numeric>{s.center_bore_mm ? `${s.center_bore_mm} mm` : "—"}</td>
            <td>{s.thread_size || "—"}</td>
            <td data-numeric>
              {s.lug_nut_torque_ftlb ? `${s.lug_nut_torque_ftlb} ft-lb` : "—"}
            </td>
            <td>{s.wheel_size || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TransmissionDirectAnswer({ vehicles, fullName }: { vehicles: any[]; fullName: string }) {
  return <TransmissionDirectAnswerInner vehicles={vehicles} fullName={fullName} />;
}

// --- Bed dimensions / fuel tank / curb weight ---

const ftIn = (n: number | null | undefined) =>
  n ? `${n} in (${(n / 12).toFixed(1)} ft)` : "—";

function BedDirectAnswer({ specs, fullName }: { specs: SpecRow[]; fullName: string }) {
  const rows = specs.filter((s) => s.bed_length_in != null);
  if (rows.length === 0) return <p>No bed dimension data available.</p>;
  const shortest = rows.reduce((b, s) =>
    (s.bed_length_in ?? 0) < (b.bed_length_in ?? 0) ? s : b,
  );
  const longest = rows.reduce((b, s) =>
    (s.bed_length_in ?? 0) > (b.bed_length_in ?? 0) ? s : b,
  );
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong> bed measures{" "}
      <strong className="text-primary text-xl">{ftIn(longest.bed_length_in)}</strong> long
      {longest.bed_config ? ` in the ${longest.bed_config}` : ""}
      {shortest !== longest ? `, or ${ftIn(shortest.bed_length_in)} in the ${shortest.bed_config}` : ""}
      {longest.bed_width_between_wheelwells_in
        ? `, with ${longest.bed_width_between_wheelwells_in} in between the wheel wells`
        : ""}
      {longest.bed_volume_cuft ? ` and ${longest.bed_volume_cuft} cu ft of cargo volume` : ""}.
    </p>
  );
}

function BedTable({ specs }: { specs: SpecRow[] }) {
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Bed</th>
          <th>Length</th>
          <th>Width</th>
          <th>Between Wheel Wells</th>
          <th>Depth</th>
          <th>Volume</th>
        </tr>
      </thead>
      <tbody>
        {specs.map((s, i) => (
          <tr key={s.id}>
            <td data-numeric>
              {s.year}
              <ConfigTag label={configLabels(specs)[i]} />
            </td>
            <td>{s.bed_config || s.trim || "—"}</td>
            <td data-numeric className="font-semibold text-primary">
              {s.bed_length_in ? `${s.bed_length_in} in` : "—"}
            </td>
            <td data-numeric>{s.bed_width_in ? `${s.bed_width_in} in` : "—"}</td>
            <td data-numeric>
              {s.bed_width_between_wheelwells_in
                ? `${s.bed_width_between_wheelwells_in} in`
                : "—"}
            </td>
            <td data-numeric>{s.bed_depth_in ? `${s.bed_depth_in} in` : "—"}</td>
            <td data-numeric>{s.bed_volume_cuft ? `${s.bed_volume_cuft} cu ft` : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Best combined MPG for a model year, used for range-on-a-tank math. */
function mpgForYear(vehicles: VehicleRow[], year: number): number | null {
  const vals = vehicles
    .filter((v) => v.year === year && v.mpg_combined)
    .map((v) => v.mpg_combined as number);
  return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
}

function FuelTankDirectAnswer({
  specs,
  vehicles,
  fullName,
}: {
  specs: SpecRow[];
  vehicles: VehicleRow[];
  fullName: string;
}) {
  const s = specs.find((r) => r.fuel_tank_gal != null);
  if (!s?.fuel_tank_gal) return <p>No fuel tank data available.</p>;
  const mpg = mpgForYear(vehicles, s.year) ?? mpgForYear(vehicles, vehicles[0]?.year ?? s.year);
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong> holds{" "}
      <strong className="text-primary text-xl">{s.fuel_tank_gal} gallons</strong> of fuel
      {s.fuel_tank_gal_optional
        ? `, or ${s.fuel_tank_gal_optional} gallons with the optional extended-range tank`
        : ""}
      {mpg
        ? `. At ${mpg} MPG combined that is roughly ${Math.round(s.fuel_tank_gal * mpg).toLocaleString()} miles of range on a full tank`
        : ""}
      .
    </p>
  );
}

function FuelTankTable({ specs, vehicles }: { specs: SpecRow[]; vehicles: VehicleRow[] }) {
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Tank Capacity</th>
          <th>Optional Tank</th>
          <th>Combined MPG</th>
          <th>Est. Range</th>
        </tr>
      </thead>
      <tbody>
        {specs.map((s) => {
          const mpg = mpgForYear(vehicles, s.year);
          return (
            <tr key={s.id}>
              <td data-numeric>{s.year}</td>
              <td data-numeric className="font-semibold text-primary">
                {s.fuel_tank_gal ? `${s.fuel_tank_gal} gal` : "—"}
              </td>
              <td data-numeric>
                {s.fuel_tank_gal_optional ? `${s.fuel_tank_gal_optional} gal` : "—"}
              </td>
              <td data-numeric>{mpg ?? "—"}</td>
              <td data-numeric>
                {mpg && s.fuel_tank_gal
                  ? `${Math.round(mpg * s.fuel_tank_gal).toLocaleString()} mi`
                  : "—"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function CurbWeightDirectAnswer({
  dimensions,
  fullName,
}: {
  dimensions: DimensionRow[];
  fullName: string;
}) {
  const [unit] = useUnitSystem();
  const rows = dimensions.filter((d) => d.curb_weight_lbs != null);
  if (rows.length === 0) return <p>No curb weight data available.</p>;
  const lightest = rows.reduce((b, d) =>
    (d.curb_weight_lbs ?? 0) < (b.curb_weight_lbs ?? 0) ? d : b,
  );
  const heaviest = rows.reduce((b, d) =>
    (d.curb_weight_lbs ?? 0) > (b.curb_weight_lbs ?? 0) ? d : b,
  );
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong> has a curb weight of{" "}
      <strong className="text-primary text-xl">
        {formatWeight(Math.round(lightest.curb_weight_lbs as number), unit)}
      </strong>
      {heaviest !== lightest
        ? ` to ${formatWeight(Math.round(heaviest.curb_weight_lbs as number), unit)} depending on body style and drivetrain`
        : ""}
      . Curb weight is the vehicle with fluids but no passengers or cargo.
    </p>
  );
}

function CurbWeightTable({ dimensions }: { dimensions: DimensionRow[] }) {
  const [unit] = useUnitSystem();
  const rows = dimensions.filter((d) => d.curb_weight_lbs != null);
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Body Style / Variant</th>
          <th>Curb Weight</th>
          <th>Wheelbase</th>
          <th>Length</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((d, i) => (
          <tr key={d.id}>
            <td data-numeric>
              {d.year}
              <ConfigTag label={configLabels(rows)[i]} />
            </td>
            <td>{d.variant_label || "—"}</td>
            <td data-numeric className="font-semibold text-primary">
              {formatWeight(Math.round(d.curb_weight_lbs as number), unit)}
            </td>
            <td data-numeric>{d.wheelbase_in ? `${d.wheelbase_in} in` : "—"}</td>
            <td data-numeric>{d.length_in ? `${d.length_in} in` : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --- NHTSA dimensions / safety and EV components ---

const inches = (n: number | null | undefined) => (n ? `${n} in` : "—");
const stars = (v: string | null | undefined) => (v ? `${v} ★` : "—");

function DimensionsDirectAnswer({
  dimensions,
  fullName,
}: {
  dimensions: DimensionRow[];
  fullName: string;
}) {
  const [unit] = useUnitSystem();
  const d = dimensions[0];
  if (!d) return <p>No dimension data available.</p>;
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong> measures{" "}
      <strong className="text-primary text-xl">{formatLength(d.length_in, unit)}</strong> long,{" "}
      {formatLength(d.width_in, unit)} wide and {formatLength(d.height_in, unit)} tall
      {d.wheelbase_in ? `, on a ${formatLength(d.wheelbase_in, unit)} wheelbase` : ""}
      {d.curb_weight_lbs ? `, weighing about ${formatWeight(d.curb_weight_lbs, unit)}` : ""}
      .
    </p>
  );
}

function SafetyDirectAnswer({ safety, fullName }: { safety: SafetyRow[]; fullName: string }) {
  const r = safety.find((s) => s.overall_rating) ?? safety[0];
  if (!r) return <p>No safety rating available.</p>;
  return (
    <p className="text-lg text-foreground">
      NHTSA rates the <strong>{fullName}</strong> at{" "}
      <strong className="text-primary text-xl">{stars(r.overall_rating)} overall</strong>
      {r.overall_front_rating ? `, ${stars(r.overall_front_rating)} frontal crash` : ""}
      {r.overall_side_rating ? `, ${stars(r.overall_side_rating)} side crash` : ""}
      {r.rollover_rating ? ` and ${stars(r.rollover_rating)} rollover` : ""}.
    </p>
  );
}

function EvDirectAnswer({ vehicles, fullName }: { vehicles: VehicleRow[]; fullName: string }) {
  const [unit] = useUnitSystem();
  const best = vehicles.reduce<VehicleRow | null>(
    (b, v) => (v.ev_range_miles && (!b || (v.ev_range_miles ?? 0) > (b.ev_range_miles ?? 0)) ? v : b),
    null,
  );
  if (!best) return <p>No electric range data available.</p>;
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong> is rated for up to{" "}
      <strong className="text-primary text-xl">{formatDistance(best.ev_range_miles, unit)}</strong> of
      electric range
      {best.charge_240v_hours
        ? `, recharging in about ${best.charge_240v_hours} hours on Level 2`
        : ""}
      .
    </p>
  );
}

function DimensionsTable({ dimensions }: { dimensions: DimensionRow[] }) {
  const [unit] = useUnitSystem();
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Body / Variant</th>
          <th>Length</th>
          <th>Width</th>
          <th>Height</th>
          <th>Wheelbase</th>
          <th>Curb Weight</th>
        </tr>
      </thead>
      <tbody>
        {dimensions.map((d, i) => (
          <tr key={d.id}>
            <td data-numeric>
              {d.year}
              <ConfigTag label={configLabels(dimensions)[i]} />
            </td>
            <td>{d.variant_label || "—"}</td>
            <td data-numeric className="font-semibold text-primary">{formatLength(d.length_in, unit)}</td>
            <td data-numeric>{formatLength(d.width_in, unit)}</td>
            <td data-numeric>{formatLength(d.height_in, unit)}</td>
            <td data-numeric>{formatLength(d.wheelbase_in, unit)}</td>
            <td data-numeric>{formatWeight(d.curb_weight_lbs, unit)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SafetyTable({ safety }: { safety: SafetyRow[] }) {
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Variant</th>
          <th>Overall</th>
          <th>Frontal</th>
          <th>Side</th>
          <th>Rollover</th>
          <th>Recalls</th>
          <th>Complaints</th>
        </tr>
      </thead>
      <tbody>
        {safety.map((r, i) => (
          <tr key={r.id}>
            <td data-numeric>
              {r.year}
              <ConfigTag label={configLabels(safety)[i]} />
            </td>
            <td>{r.variant_label}</td>
            <td data-numeric className="font-semibold text-primary">{stars(r.overall_rating)}</td>
            <td data-numeric>{stars(r.overall_front_rating)}</td>
            <td data-numeric>{stars(r.overall_side_rating)}</td>
            <td data-numeric>{stars(r.rollover_rating)}</td>
            <td data-numeric>{r.recalls_count ?? "—"}</td>
            <td data-numeric>{r.nhtsa_complaints_count ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EvTable({ vehicles }: { vehicles: VehicleRow[] }) {
  const [unit] = useUnitSystem();
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Trim</th>
          <th>Motor</th>
          <th>Range</th>
          <th>City Range</th>
          <th>Hwy Range</th>
          <th>120V Charge</th>
          <th>240V Charge</th>
        </tr>
      </thead>
      <tbody>
        {vehicles.map((v, i) => (
          <tr key={v.id}>
            <td data-numeric>
              {v.year}
              <ConfigTag label={configLabels(vehicles)[i]} />
            </td>
            <td>{v.trim || "—"}</td>
            <td>{v.ev_motor || "—"}</td>
            <td data-numeric className="font-semibold text-primary">
              {formatDistance(v.ev_range_miles, unit)}
            </td>
            <td data-numeric>{formatDistance(v.ev_range_city, unit)}</td>
            <td data-numeric>{formatDistance(v.ev_range_hwy, unit)}</td>
            <td data-numeric>{v.charge_120v_hours ? `${v.charge_120v_hours} h` : "—"}</td>
            <td data-numeric>{v.charge_240v_hours ? `${v.charge_240v_hours} h` : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TransmissionDirectAnswerInner({ vehicles, fullName }: { vehicles: any[]; fullName: string }) {
  const list = uniqueValues(vehicles, "transmission");
  if (list.length === 0) return <p>No transmission data available.</p>;
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong> comes with{" "}
      <strong className="text-primary">{list.join(", ")}</strong>
      {list.length > 1 ? " depending on engine and trim." : "."}
    </p>
  );
}

function DrivetrainDirectAnswer({ vehicles, fullName }: { vehicles: any[]; fullName: string }) {
  const list = uniqueValues(vehicles, "drive");
  if (list.length === 0) return <p>No drivetrain data available.</p>;
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong> is offered in{" "}
      <strong className="text-primary">{list.join(", ")}</strong>
      {list.length > 1 ? " configurations, depending on trim." : " configuration."}
    </p>
  );
}

function VehicleClassDirectAnswer({ vehicles, fullName }: { vehicles: any[]; fullName: string }) {
  const list = uniqueValues(vehicles, "vehicle_class");
  if (list.length === 0) return <p>No class data available.</p>;
  return (
    <p className="text-lg text-foreground">
      The <strong>{fullName}</strong> is classified by the EPA as a{" "}
      <strong className="text-primary">{list.join(" / ")}</strong>.
    </p>
  );
}

function TransmissionTable({ vehicles }: { vehicles: any[] }) {
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Trim</th>
          <th>Engine</th>
          <th>Transmission</th>
          <th>Drive</th>
          <th>MPG Comb.</th>
        </tr>
      </thead>
      <tbody>
        {vehicles.map((v, i) => (
          <tr key={v.id}>
            <td data-numeric>
              {v.year}
              <ConfigTag label={configLabels(vehicles)[i]} />
            </td>
            <td>{v.trim || "—"}</td>
            <td>
              {v.cylinders || "—"}{v.displacement ? ` ${v.displacement}L` : ""}
            </td>
            <td className="font-semibold text-primary">{v.transmission || "—"}</td>
            <td>{v.drive || "—"}</td>
            <td data-numeric>{v.mpg_combined ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DrivetrainTable({ vehicles }: { vehicles: any[] }) {
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Trim</th>
          <th>Drive</th>
          <th>Transmission</th>
          <th>Engine</th>
          <th>MPG Comb.</th>
        </tr>
      </thead>
      <tbody>
        {vehicles.map((v, i) => (
          <tr key={v.id}>
            <td data-numeric>
              {v.year}
              <ConfigTag label={configLabels(vehicles)[i]} />
            </td>
            <td>{v.trim || "—"}</td>
            <td className="font-semibold text-primary">{v.drive || "—"}</td>
            <td>{v.transmission || "—"}</td>
            <td>
              {v.cylinders || "—"}{v.displacement ? ` ${v.displacement}L` : ""}
            </td>
            <td data-numeric>{v.mpg_combined ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function VehicleClassTable({ vehicles }: { vehicles: any[] }) {
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Trim</th>
          <th>Vehicle Class</th>
          <th>Drive</th>
          <th>Fuel Type</th>
          <th>MPG Comb.</th>
        </tr>
      </thead>
      <tbody>
        {vehicles.map((v, i) => (
          <tr key={v.id}>
            <td data-numeric>
              {v.year}
              <ConfigTag label={configLabels(vehicles)[i]} />
            </td>
            <td>{v.trim || "—"}</td>
            <td className="font-semibold text-primary">{v.vehicle_class || "—"}</td>
            <td>{v.drive || "—"}</td>
            <td>{v.fuel_type_detail || v.fuel_type || "—"}</td>
            <td data-numeric>{v.mpg_combined ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MpgTable({ vehicles }: { vehicles: any[] }) {
  const [unit] = useUnitSystem();
  const unitLabel = unit === "metric" ? "L/100km" : "MPG";
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Trim</th>
          <th>Engine</th>
          <th>Fuel</th>
          <th>City ({unitLabel})</th>
          <th>Hwy ({unitLabel})</th>
          <th>Combined ({unitLabel})</th>
        </tr>
      </thead>
      <tbody>
        {vehicles.map((v, i) => (
          <tr key={v.id}>
            <td data-numeric>
              {v.year}
              <ConfigTag label={configLabels(vehicles)[i]} />
            </td>
            <td>{v.trim || "—"}</td>
            <td>
              {v.cylinders || "—"}{v.displacement ? ` ${v.displacement}L` : ""}
            </td>
            <td>{v.fuel_type_detail || v.fuel_type || "—"}</td>
            <td data-numeric>{formatMpgValue(v.mpg_city, unit)}</td>
            <td data-numeric>{formatMpgValue(v.mpg_hwy, unit)}</td>
            <td data-numeric className="font-semibold text-primary">
              {formatMpgValue(v.mpg_combined, unit)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EngineTable({ vehicles }: { vehicles: any[] }) {
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Trim</th>
          <th>Cylinders</th>
          <th>Displ.</th>
          <th>Transmission</th>
          <th>Drive</th>
          <th>Fuel Type</th>
          <th>Vehicle Class</th>
        </tr>
      </thead>
      <tbody>
        {vehicles.map((v, i) => (
          <tr key={v.id}>
            <td data-numeric>
              {v.year}
              <ConfigTag label={configLabels(vehicles)[i]} />
            </td>
            <td>{v.trim || "—"}</td>
            <td data-numeric>{v.cylinders || "—"}</td>
            <td data-numeric>{v.displacement ? `${v.displacement}L` : "—"}</td>
            <td>{v.transmission || "—"}</td>
            <td>{v.drive || "—"}</td>
            <td>{v.fuel_type_detail || v.fuel_type || "—"}</td>
            <td>{v.vehicle_class || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FuelCostTable({ vehicles }: { vehicles: any[] }) {
  return (
    <table className="spec-table">
      <thead>
        <tr>
          <th>Year</th>
          <th>Trim</th>
          <th>Engine</th>
          <th>Fuel</th>
          <th>MPG Comb.</th>
          <th>Annual Cost</th>
          <th>CO₂ (g/mi)</th>
          <th>GHG Score</th>
        </tr>
      </thead>
      <tbody>
        {vehicles.map((v, i) => (
          <tr key={v.id}>
            <td data-numeric>
              {v.year}
              <ConfigTag label={configLabels(vehicles)[i]} />
            </td>
            <td>{v.trim || "—"}</td>
            <td>
              {v.cylinders || "—"}{v.displacement ? ` ${v.displacement}L` : ""}
            </td>
            <td>{v.fuel_type_detail || v.fuel_type || "—"}</td>
            <td data-numeric>{v.mpg_combined ?? "—"}</td>
            <td data-numeric className="font-semibold text-primary">
              {v.annual_fuel_cost ? `$${v.annual_fuel_cost.toLocaleString()}` : "—"}
            </td>
            <td data-numeric>{v.co2_tailpipe_gpm ?? "—"}</td>
            <td data-numeric>{v.ghg_score ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --- FAQ ---

export function buildDataTypeFaqs({
  fullName,
  dataType,
  vehicles,
  specs,
  dimensions,
  safety,
  latestYear,
  makeSlug,
  makeName,
}: {
  fullName: string;
  dataType: DataTypeKey;
  vehicles: any[];
  specs: SpecRow[];
  dimensions: DimensionRow[];
  safety: SafetyRow[];
  latestYear: number | null;
  makeSlug?: string;
  makeName?: string;
}): { q: string; a: string }[] {
  const faqs: { q: string; a: string }[] = []; const __fy = latestYear ? vehicles.filter((v) => v.year === latestYear) : vehicles;

  if (dataType === "dimensions") {
    const d = dimensions[0];
    faqs.push({
      q: `How long is the ${fullName}?`,
      a: d?.length_in
        ? `The ${fullName} is ${d.length_in} inches long${d.width_in ? `, ${d.width_in} inches wide` : ""}${d.height_in ? ` and ${d.height_in} inches tall` : ""}. That is roughly ${(d.length_in / 12).toFixed(1)} feet — compare it with your garage depth before buying.`
        : `Length for the ${fullName} varies by body style. See the table above.`,
    });
    faqs.push({
      q: `Will the ${fullName} fit in a standard garage?`,
      a: `A standard single-car garage is about 20 feet (240 in) deep and 10 feet (120 in) wide. ${d?.length_in ? `At ${d.length_in} in long${d.width_in ? ` and ${d.width_in} in wide` : ""}, the ${fullName} fits with ${Math.max(0, Math.round(240 - d.length_in))} in of room to spare front-to-back` : `Check the length in the table above against your own measurements`}. Remember to allow for mirrors and door swing.`,
    });
    faqs.push({
      q: `How much does the ${fullName} weigh?`,
      a: d?.curb_weight_lbs
        ? `Curb weight for the ${fullName} is about ${Math.round(d.curb_weight_lbs).toLocaleString()} lbs. Curb weight excludes passengers and cargo, so add roughly 150 lbs per occupant when calculating payload.`
        : `Curb weight varies by trim and drivetrain — see the table above for the figures we have verified.`,
    });
  } else if (dataType === "safety-ratings") {
    const r = safety.find((s) => s.overall_rating) ?? safety[0];
    faqs.push({
      q: `Is the ${fullName} a safe car?`,
      a: r?.overall_rating
        ? `NHTSA gives the ${fullName} an overall crash rating of ${r.overall_rating} out of 5 stars${r.overall_front_rating ? `, including ${r.overall_front_rating} stars in the frontal crash test` : ""}${r.overall_side_rating ? ` and ${r.overall_side_rating} stars in the side crash test` : ""}. Ratings are per body style and model year — check the table above for yours.`
        : `NHTSA star ratings for the ${fullName} vary by model year and body style. See the table above.`,
    });
    faqs.push({
      q: `What is the rollover rating of the ${fullName}?`,
      a: r?.rollover_rating
        ? `The ${fullName} scores ${r.rollover_rating} out of 5 stars for rollover resistance${r.rollover_possibility ? `, with a calculated rollover risk of about ${(r.rollover_possibility * 100).toFixed(1)}%` : ""}. Taller vehicles typically score lower here than sedans.`
        : `Rollover resistance is not published for every ${fullName} configuration. See the table above.`,
    });
    faqs.push({
      q: `Does the ${fullName} have driver assistance features?`,
      a: r
        ? `NHTSA records the tested ${fullName} as ${r.has_esc ? "having" : "not listing"} electronic stability control, ${r.has_forward_collision_warning ? "with" : "without"} forward collision warning and ${r.has_lane_departure_warning ? "with" : "without"} lane departure warning. Availability varies by trim and package.`
        : `Driver assistance availability varies by trim — check the window sticker for your specific vehicle.`,
    });
  } else if (dataType === "ev-range") {
    const best = __fy.reduce(
      (b: any, v: any) => (v.ev_range_miles && (!b || v.ev_range_miles > b.ev_range_miles) ? v : b),
      null as any,
    );
    faqs.push({
      q: `How far can the ${fullName} go on a charge?`,
      a: best?.ev_range_miles
        ? `The ${fullName} is EPA-rated for up to ${best.ev_range_miles} miles of electric range${latestYear ? ` (${latestYear} model year)` : ""}. Real-world range drops in cold weather, at highway speeds, and with heavy climate control use — plan for roughly 70–80% of the rated figure in winter.`
        : `Electric range for the ${fullName} varies by model year and battery size. See the table above.`,
    });
    faqs.push({
      q: `How long does the ${fullName} take to charge?`,
      a: best?.charge_240v_hours
        ? `On a 240-volt Level 2 charger the ${fullName} takes about ${best.charge_240v_hours} hours for a full charge${best.charge_120v_hours ? `, versus roughly ${best.charge_120v_hours} hours on a standard 120-volt household outlet` : ""}. DC fast charging, where supported, is far quicker but is not part of the EPA data set.`
        : `Charge times depend on the onboard charger and the outlet used — see the table above for the figures we have verified.`,
    });
  }

  if (dataType === "towing-capacity") {
    const max = specs.reduce<SpecRow | null>(
      (b, s) => ((s.towing_capacity_lbs ?? 0) > (b?.towing_capacity_lbs ?? 0) ? s : b),
      null,
    );
    faqs.push({
      q: `How much can a ${fullName} tow?`,
      a: max?.towing_capacity_lbs
        ? `The ${fullName} can tow up to ${max.towing_capacity_lbs.toLocaleString()} lbs${
            max.trim ? ` when equipped as the ${max.trim}` : ""
          }${max.towing_config ? ` with the ${max.towing_config} configuration` : ""}. Capacity varies by engine, axle ratio, cab and bed configuration — see the year-by-year table above.`
        : `Towing capacity for the ${fullName} varies by configuration. See the table above.`,
    });
    faqs.push({
      q: `What is the payload capacity of the ${fullName}?`,
      a: max?.payload_capacity_lbs
        ? `Maximum payload for the ${fullName} is around ${max.payload_capacity_lbs.toLocaleString()} lbs. Payload is reduced by passengers, cargo, and the tongue weight of any trailer you are pulling.`
        : `Payload figures depend on trim and options — always check the yellow sticker on the driver's door jamb of your specific vehicle.`,
    });
    faqs.push({
      q: `Do I need a weight distribution hitch with the ${fullName}?`,
      a: `Most manufacturers require a weight distribution hitch once trailer weight exceeds roughly 5,000 lbs, and tongue weight should stay near 10–15% of the loaded trailer weight. Confirm the exact threshold in your owner's manual before towing.`,
    });
  } else if (dataType === "bed-dimensions") {
    const beds = specs.filter((s) => s.bed_length_in != null);
    const longest = beds.length
      ? beds.reduce((b, s) => ((s.bed_length_in ?? 0) > (b.bed_length_in ?? 0) ? s : b))
      : null;
    faqs.push({
      q: `How long is the ${fullName} bed?`,
      a: longest?.bed_length_in
        ? `The ${fullName} bed measures ${longest.bed_length_in} inches (${(longest.bed_length_in / 12).toFixed(1)} ft) at the floor${longest.bed_config ? ` in the ${longest.bed_config}` : ""}. Shorter bed configurations are listed year by year in the table above.`
        : `Bed length for the ${fullName} depends on the configuration. See the table above.`,
    });
    faqs.push({
      q: `Will a 4x8 sheet of plywood fit in a ${fullName}?`,
      a: longest?.bed_width_between_wheelwells_in
        ? `The ${fullName} measures ${longest.bed_width_between_wheelwells_in} inches between the wheel wells, so a 48-inch sheet ${longest.bed_width_between_wheelwells_in >= 48 ? "lies flat between them" : "will not sit flat between them — it has to rest on top of the wheel wells or ride at an angle"}. With the tailgate down and the load properly flagged, an 8-foot sheet will overhang on any short bed.`
        : `Check the width between the wheel wells in the table above — a plywood sheet needs 48 inches to lie flat.`,
    });
    faqs.push({
      q: `How much cargo volume does the ${fullName} bed have?`,
      a: longest?.bed_volume_cuft
        ? `Cargo volume is about ${longest.bed_volume_cuft} cubic feet${longest.bed_depth_in ? ` with a ${longest.bed_depth_in}-inch bed depth` : ""}. Volume is measured to the top of the bed rails, so a level load will be less.`
        : `Cargo volume varies by bed configuration. See the table above.`,
    });
  } else if (dataType === "fuel-tank-capacity") {
    const t = specs.find((s) => s.fuel_tank_gal != null);
    const mpgVals = vehicles
      .filter((v: any) => v.mpg_combined)
      .map((v: any) => v.mpg_combined as number);
    const mpg = mpgVals.length
      ? Math.round(mpgVals.reduce((a, b) => a + b, 0) / mpgVals.length)
      : null;
    faqs.push({
      q: `How big is the ${fullName} gas tank?`,
      a: t?.fuel_tank_gal
        ? `The ${fullName} has a ${t.fuel_tank_gal}-gallon fuel tank${t.fuel_tank_gal_optional ? `, or ${t.fuel_tank_gal_optional} gallons with the optional extended-range tank` : ""}. Capacity changed between generations — the table above lists it year by year.`
        : `Fuel tank capacity for the ${fullName} varies by model year. See the table above.`,
    });
    faqs.push({
      q: `How far can the ${fullName} go on a full tank?`,
      a: t?.fuel_tank_gal && mpg
        ? `At roughly ${mpg} MPG combined, a full ${t.fuel_tank_gal}-gallon tank is good for about ${Math.round(t.fuel_tank_gal * mpg).toLocaleString()} miles. Plan on refueling near 300 miles, since most fuel gauges read empty with 1–2 gallons still in the tank.`
        : `Range depends on the engine and how you drive — multiply the tank size above by your real-world MPG.`,
    });
    faqs.push({
      q: `How much does it cost to fill up a ${fullName}?`,
      a: t?.fuel_tank_gal
        ? `At $3.50 per gallon, filling the ${t.fuel_tank_gal}-gallon tank from empty costs about $${Math.round(t.fuel_tank_gal * 3.5).toLocaleString()}. Multiply the tank size by your local pump price for a current figure.`
        : `Multiply the tank capacity above by your local fuel price to estimate a fill-up.`,
    });
  } else if (dataType === "curb-weight") {
    const rows = dimensions.filter((d) => d.curb_weight_lbs != null);
    const lo = rows.length ? Math.round(Math.min(...rows.map((d) => d.curb_weight_lbs as number))) : null;
    const hi = rows.length ? Math.round(Math.max(...rows.map((d) => d.curb_weight_lbs as number))) : null;
    faqs.push({
      q: `How much does a ${fullName} weigh?`,
      a: lo
        ? `The ${fullName} weighs ${lo.toLocaleString()}${hi !== lo ? ` to ${hi?.toLocaleString()}` : ""} lbs at the curb, depending on body style, drivetrain, and model year. The table above breaks it out year by year.`
        : `Curb weight for the ${fullName} varies by trim. See the table above.`,
    });
    faqs.push({
      q: `What is the difference between curb weight and gross vehicle weight?`,
      a: `Curb weight is the vehicle as it sits with fluids and a full tank but nobody aboard. Gross vehicle weight rating (GVWR) is the maximum the vehicle may weigh fully loaded — the difference between the two is your payload allowance for passengers, cargo, and trailer tongue weight.`,
    });
    faqs.push({
      q: `Why does curb weight matter?`,
      a: `Weight drives fuel economy, braking distance, tire and brake wear, and how much payload is left over. It also matters for ferry and bridge limits, tow-dolly ratings, and some parking structures.`,
    });
  } else if (dataType === "tire-size") {
    const s = specs[0];
    faqs.push({
      q: `What size tires does the ${fullName} take?`,
      a: s?.front_tire_size
        ? `The ${fullName} uses ${s.front_tire_size} tires${
            s.rear_tire_size && s.rear_tire_size !== s.front_tire_size
              ? ` up front and ${s.rear_tire_size} at the rear`
              : ""
          }${s.wheel_size ? ` on ${s.wheel_size} wheels` : ""}${
            latestYear ? ` for the ${latestYear} model year` : ""
          }. Sizes differ between trims and years — the table above lists every fitment we have verified.`
        : `Tire sizes for the ${fullName} vary by trim and model year. See the table above.`,
    });
    faqs.push({
      q: `What tire pressure should the ${fullName} run?`,
      a: s?.tire_pressure_front_psi
        ? `The recommended cold tire pressure is ${s.tire_pressure_front_psi} PSI front${
            s.tire_pressure_rear_psi ? ` and ${s.tire_pressure_rear_psi} PSI rear` : ""
          }. Always follow the placard on the driver's door jamb rather than the max pressure printed on the tire sidewall.`
        : `Use the pressure listed on the driver's door-jamb placard, not the maximum pressure printed on the tire sidewall.`,
    });
    faqs.push({
      q: `Can I put a different tire size on my ${fullName}?`,
      a: `Plus-sizing is possible as long as the new tire's overall diameter stays within about 3% of the OEM size, otherwise the speedometer, ABS, and stability control calibration are affected. Also confirm the load index and speed rating meet or exceed the factory specification.`,
    });
  } else if (dataType === "bolt-pattern") {
    const s = specs[0];
    faqs.push({
      q: `What is the bolt pattern on a ${fullName}?`,
      a: s?.bolt_pattern
        ? `The ${fullName} uses a ${s.bolt_pattern} bolt pattern (lug count x pitch circle diameter in mm)${
            s.center_bore_mm ? ` with a ${s.center_bore_mm} mm center bore` : ""
          }. Check the table above — the pattern can change between generations.`
        : `Bolt pattern for the ${fullName} varies by model year. See the table above.`,
    });
    faqs.push({
      q: `What is the lug nut torque for the ${fullName}?`,
      a: s?.lug_nut_torque_ftlb
        ? `Torque the lug nuts to ${s.lug_nut_torque_ftlb} ft-lb${
            s.thread_size ? ` (${s.thread_size} threads)` : ""
          } in a star pattern, then re-check after roughly 50 miles of driving.`
        : `Follow the torque value in your owner's manual and tighten in a star pattern, re-checking after about 50 miles.`,
    });
    faqs.push({
      q: `Will wheels from another vehicle fit my ${fullName}?`,
      a: `A wheel fits only when the bolt pattern, center bore, and offset all work together. A matching bolt pattern with a smaller center bore will not seat, and a large offset change can cause rubbing or altered steering feel — hub-centric rings can cover a bore that is too large, but never a bore that is too small.`,
    });
  }

  if (dataType === "mpg") {
    const best = __fy.reduce(
      (b, v) => (v.mpg_combined && (!b || v.mpg_combined > b.mpg_combined) ? v : b),
      __fy[0] || vehicles[0],
    );
    faqs.push({
      q: `What is the MPG of the ${fullName}?`,
      a: `The ${fullName} gets up to ${best?.mpg_combined ?? "—"} MPG combined (${best?.mpg_city ?? "—"} city / ${best?.mpg_hwy ?? "—"} highway)${
        latestYear ? ` in the ${latestYear} model year` : ""
      }. Fuel economy varies by engine and trim — see the full table above for details.`,
    });
    faqs.push({
      q: `Which ${fullName} trim has the best fuel economy?`,
      a: best?.trim
        ? `The ${best.trim} trim offers the best combined fuel economy at ${best.mpg_combined} MPG. Smaller engines and front-wheel-drive configurations typically achieve the highest MPG.`
        : `Fuel economy varies by trim and engine. See the table above for a full comparison.`,
    });
  } else if (dataType === "engine") {
    faqs.push({
      q: `What engine does the ${fullName} have?`,
      a: `The ${fullName} is available with ${vehicles[0]?.cylinders || "various"} cylinder engine${
        vehicles[0]?.displacement ? `s ranging up to ${vehicles[0].displacement}L` : ""
      }. Transmission and drivetrain options vary by trim. See the full table above.`,
    });
    faqs.push({
      q: `Is the ${fullName} available with all-wheel drive?`,
      a: vehicles.some((v) => v.drive?.toLowerCase().includes("all") || v.drive?.toLowerCase().includes("4wd") || v.drive?.toLowerCase().includes("awd"))
        ? `Yes, the ${fullName} is available with all-wheel drive. Check the "Drive" column in the table above for specific trims.`
        : `Check the drive column in the table above to see available drivetrain configurations.`,
    });
  } else if (dataType === "fuel-cost") {
    const cheapest = vehicles.reduce(
      (b, v) => (v.annual_fuel_cost && (!b || v.annual_fuel_cost < b.annual_fuel_cost) ? v : b),
      __fy[0] || vehicles[0],
    );
    faqs.push({
      q: `How much does it cost to fuel the ${fullName}?`,
      a: `The estimated annual fuel cost for the ${fullName} is ${
        cheapest?.annual_fuel_cost ? `$${cheapest.annual_fuel_cost.toLocaleString()}` : "varies"
      } per year, based on 15,000 miles of driving and average fuel prices. Costs vary by trim and engine.`,
    });
    faqs.push({
      q: `How much CO₂ does the ${fullName} produce?`,
      a: `The ${fullName} produces approximately ${cheapest?.co2_tailpipe_gpm ?? "—"} grams of CO₂ per mile. Lower numbers mean fewer emissions. See the table for per-trim details.`,
    });
  } else if (dataType === "transmission") {
    const list = uniqueValues(vehicles, "transmission");
    faqs.push({
      q: `What transmission does the ${fullName} have?`,
      a: list.length
        ? `The ${fullName} is offered with ${list.join(", ")}${latestYear ? ` (${latestYear} and earlier model years)` : ""}. Availability depends on engine and trim — see the year-by-year table above.`
        : `Transmission options for the ${fullName} vary by model year and trim. See the table above.`,
    });
    faqs.push({
      q: `Does the ${fullName} come with a manual transmission?`,
      a: list.some((t) => t.toLowerCase().includes("manual"))
        ? `Yes — a manual transmission is listed for at least one ${fullName} configuration. Check the table above for the exact years and trims.`
        : `No manual transmission is listed for the ${fullName} in the EPA data we track; all listed configurations are automatic or CVT.`,
    });
  } else if (dataType === "drivetrain") {
    const list = uniqueValues(vehicles, "drive");
    faqs.push({
      q: `Is the ${fullName} front-wheel, rear-wheel, or all-wheel drive?`,
      a: list.length
        ? `The ${fullName} is available in ${list.join(", ")}. Drivetrain availability changes by model year and trim — the table above shows every combination.`
        : `Drivetrain configurations for the ${fullName} vary by model year. See the table above.`,
    });
    faqs.push({
      q: `Does the ${fullName} come with AWD or 4WD?`,
      a: list.some((d) => /all|awd|4wd|4-wheel|four/i.test(d))
        ? `Yes — all-wheel or four-wheel drive is available on the ${fullName}. See the table above for the specific years and trims.`
        : `The ${fullName} is not listed with all-wheel or four-wheel drive in the EPA data we track.`,
    });
  } else if (dataType === "vehicle-class") {
    const list = uniqueValues(vehicles, "vehicle_class");
    faqs.push({
      q: `What class of vehicle is the ${fullName}?`,
      a: list.length
        ? `The EPA classifies the ${fullName} as a ${list.join(" / ")}. This classification is based on interior volume and body style and affects how its fuel economy is compared with other vehicles.`
        : `EPA class data for the ${fullName} varies by model year. See the table above.`,
    });
    faqs.push({
      q: `Is the ${fullName} considered a small or large vehicle?`,
      a: list.length
        ? `Based on its EPA class (${list.join(", ")}), the ${fullName} is grouped with vehicles of similar size and interior volume. The class can change between model years when the body is redesigned.`
        : `Size classification for the ${fullName} depends on the model year — see the table above.`,
    });
  } else if (dataType === "maintenance-schedule") {
    const ctx = maintenanceContext({ specs, dimensions, makeSlug, makeName });
    const p = buildMaintenanceProfile(vehicles, ctx);
    const econ = buildMaintenanceEconomics(p, ctx);
    const rotation = p.items.find((i) => i.service === "Tire rotation");
    const trans = p.items.find((i) => /transmission fluid|cvt fluid/i.test(i.service));
    faqs.push({
      q: `How often does a ${fullName} need an oil change?`,
      a: p.isElectric
        ? `The ${fullName} is electric and has no engine oil to change. Its recurring services are tire rotation every ${(rotation?.intervalMiles ?? 7500).toLocaleString()} miles, brake fluid every 3 years, cabin air filter every 15,000 miles, and a battery/inverter coolant check around every 30,000 miles.`
        : `The ${fullName} needs an oil and filter change every ${p.oilIntervalMiles.toLocaleString()} miles or 12 months, whichever comes first. ${p.oilReason}`,
    });
    faqs.push({
      q: `What is the ${fullName} maintenance schedule by mileage?`,
      a: `${p.items
        .filter((i) => i.intervalMiles > 0)
        .sort((a, b) => a.intervalMiles - b.intervalMiles)
        .slice(0, 7)
        .map((i) => `${i.service.toLowerCase()} every ${i.intervalMiles.toLocaleString()} miles`)
        .join(", ")}. That is ${econ.eventsTo100k} separate service events in the first 100,000 miles for this ${p.powertrain} ${
        trans ? trans.service.replace(/ fluid$/i, "").toLowerCase() : "drivetrain"
      } configuration. The table above lists every interval with the services that come due at each milestone.`,
    });
    faqs.push({
      q: `How much does it cost to maintain a ${fullName}?`,
      a: `Costing this model's own interval list at independent-shop rates comes to roughly $${econ.costTo100kLow.toLocaleString()}–$${econ.costTo100kHigh.toLocaleString()} over 100,000 miles, or about $${econ.costPer10kLow}–$${econ.costPer10kHigh} per 10,000 miles${
        econ.oilChangesTo100k > 0
          ? `, of which ${econ.oilChangesTo100k} oil and filter changes are the single biggest recurring line`
          : `, with no oil service in the list at all`
      }. Tires (typically every 40,000–60,000 miles) sit on top of that. Ask your shop to quote against the interval list above rather than an upsold "package".`,
    });
    faqs.push({
      q: `Is skipping a service on the ${fullName} a problem?`,
      a: `Some intervals are forgiving and some are not. Cabin filters and wiper blades are comfort items, but on this configuration the expensive ones to defer are ${
        p.isElectric
          ? "brake fluid and the traction-battery coolant loop"
          : `brake fluid, coolant and ${trans ? trans.service.toLowerCase() : "transmission fluid"}${
              ctx.towingLbs && ctx.towingLbs >= 5000
                ? ` — especially on a vehicle rated to tow ${ctx.towingLbs.toLocaleString()} lbs, where fluid heat is the failure mode`
                : ""
            }`
      }. The item that comes due most often here is ${econ.shortestIntervalService.toLowerCase()} at every ${econ.shortestIntervalMiles.toLocaleString()} miles. Keep records — a documented service history also protects resale value and warranty claims.`,
    });
  }

  return faqs;
}

/**
 * Analysis prose generated from this model's own numbers. It gives the page
 * substance beyond the table (and lifts the text-to-HTML ratio auditors read),
 * without repeating a template across models — see src/lib/spec-narrative.ts.
 */
function SpecNarrative(props: {
  fullName: string;
  makeName: string;
  dataType: DataTypeKey;
  makeSlug: string;
  modelSlug: string;
  maintenanceCtx?: ReturnType<typeof maintenanceContext>;
  vehicles: VehicleRow[];
  specs: SpecRow[];
  dimensions: DimensionRow[];
  safety: SafetyRow[];
}) {
  const maintDepth =
    props.dataType === "maintenance-schedule"
      ? buildMaintenanceModelDepth({
          fullName: props.fullName,
          makeName: props.makeName,
          vehicles: props.vehicles,
          ctx: props.maintenanceCtx ?? {},
        })
      : [];
  const depth = maintDepth.length ? maintDepth : buildDepthNarrative(props);
  const base =
    props.dataType === "safety-ratings" && depth.length === 0
      ? [...buildSpecNarrative(props), ...buildSafetyNarrative(props.fullName, props.safety)]
      : buildSpecNarrative(props);
  // Depth pages already carry sourcing and how-to-read guidance in their own
  // voice; keeping the generic blocks too would duplicate templated prose
  // across every page in the cluster.
  const sections = depth.length
    ? [...depth, ...base.filter((s) => !/^(How to read this|Where these)/.test(s.heading))]
    : base;
  if (sections.length === 0) return null;
  return (
    <section className="mx-auto max-w-6xl px-4 sm:px-6 py-6">
      <div className="rounded-lg border border-border bg-card p-5 sm:p-6">
        <h2 className="text-lg font-semibold text-foreground">
          {props.fullName} data analysis
        </h2>
        <div className="mt-4 space-y-5 max-w-3xl">
          {sections.map((s) => (
            <div key={s.heading}>
              <h3 className="text-base font-medium text-foreground">{s.heading}</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
